import { useState, useEffect } from "react";
import { useStudioUser } from "../../hooks/useStudioUser";
import { useAuth } from "../../contexts/AuthContext";
import {
  PencilIcon,
  TrashIcon,
  PlusIcon,
  CheckIcon,
  XMarkIcon,
  Bars3Icon,
  ChevronDownIcon,
  ChevronRightIcon,
  ArrowTopRightOnSquareIcon,
} from "@heroicons/react/24/outline";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import Notification from "../Notification";
import Tooltip from "../Tooltip";
import UpgradePrompt from "../UpgradePrompt";
import { useStudioPlan } from "../../hooks/useStudioPlan";
import { Link } from "react-router-dom";
import SubAccountManagement from "./SubAccountManagement";
import StudioClosureDates from "./StudioClosureDates";
import DOMPurify from 'dompurify';

// Security constants
const MAX_LENGTHS: Record<string, number> = {
  beltName: 100,
  roleName: 100,
  description: 500,
  membershipName: 100,
  membershipPrice: 10, // For price input validation
  curriculumTitle: 200,
  curriculumDescription: 1000,
};

const SUBMISSION_COOLDOWN = 2000; // 2 seconds between submissions

// Input sanitization function
const sanitizeInput = (input: string, fieldType?: string): string => {
  if (typeof input !== 'string') return '';
  
  let sanitized = input;
  
  // Remove dangerous patterns
  sanitized = sanitized.replace(/[<>]/g, ''); // Remove HTML tags
  sanitized = sanitized.replace(/javascript:/gi, ''); // Remove javascript protocol
  sanitized = sanitized.replace(/vbscript:/gi, ''); // Remove vbscript protocol
  sanitized = sanitized.replace(/on\w+\s*=/gi, ''); // Remove event handlers
  sanitized = sanitized.replace(/data:text\/html/gi, ''); // Remove data URLs
  
  // For membership names, belt names, role names, and curriculum fields, preserve spaces
  if (fieldType === 'membershipName' || fieldType === 'beltName' || fieldType === 'roleName' || 
      fieldType === 'curriculumTitle' || fieldType === 'curriculumDescription') {
    return sanitized; // Don't trim, preserve spaces
  }
  
  return sanitized.trim();
};

// Enhanced validation function
const validateInput = (value: string, type: string): boolean => {
  if (!value || typeof value !== 'string') return false;
  
  const sanitized = sanitizeInput(value, type);
  if (sanitized.length === 0) return false;

  // Check length limits
  if (MAX_LENGTHS[type] && sanitized.length > MAX_LENGTHS[type]) {
    return false;
  }
  
  // Type-specific validation
  switch (type) {
    case 'beltName':
    case 'roleName':
    case 'membershipName':
      // Allow letters, numbers, spaces, hyphens, apostrophes for membership names
      const membershipNameDangerousPatterns = /[<>]|javascript:|vbscript:|on\w+\s*=/i;
      return !membershipNameDangerousPatterns.test(sanitized) && sanitized.length >= 1;
    case 'description':
      // Allow most characters for descriptions, just exclude dangerous ones
      const dangerousPatterns = /[<>]|javascript:|vbscript:|on\w+\s*=/i;
      return !dangerousPatterns.test(sanitized) && sanitized.length >= 1;
    case 'membershipPrice':
      // Validate price format
      const priceRegex = /^\d+(\.\d{1,2})?$/;
      return priceRegex.test(sanitized) && !isNaN(Number(sanitized)) && Number(sanitized) >= 0;
    default:
      return sanitized.length > 0;
  }
};

// Security event logging
const logSecurityEvent = (event: string, details: any) => {
  console.warn(`Security Event: ${event}`, {
    timestamp: new Date().toISOString(),
    userAgent: navigator.userAgent,
    details
  });
};

function AddItemModal({
  open,
  onClose,
  onSave,
  label,
}: {
  open: boolean;
  onClose: () => void;
  onSave: (name: string) => void;
  label: string;
}) {
  const [value, setValue] = useState("");
  const [validationError, setValidationError] = useState("");
  
  useEffect(() => {
    if (open) {
      setValue("");
      setValidationError("");
    }
  }, [open]);
  
  const handleInputChange = (inputValue: string) => {
    // Sanitize and validate input
    const sanitized = sanitizeInput(inputValue, 'beltName');
    
    // Check length limit
    if (sanitized.length > MAX_LENGTHS.beltName) {
      setValidationError(`Maximum ${MAX_LENGTHS.beltName} characters allowed`);
      return;
    }
    
    // Validate input
    if (sanitized && !validateInput(sanitized, 'beltName')) {
      setValidationError("Invalid characters detected");
      return;
    }
    
    setValidationError("");
    setValue(sanitized);
  };
  
  const handleSave = () => {
    if (!value.trim()) {
      setValidationError("This field is required");
      return;
    }
    
    if (!validateInput(value, 'beltName')) {
      setValidationError("Invalid input detected");
      logSecurityEvent('INVALID_INPUT_ATTEMPT', { field: 'beltName', value: value.substring(0, 50) });
      return;
    }
    
    onSave(value.trim());
    onClose();
  };
  
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 w-full max-w-sm">
        <h3 className="text-lg font-medium mb-4 text-gray-900 dark:text-white">
          Add {label}
        </h3>
        <input
          className={getInputClassName(
            "border border-gray-300 dark:border-gray-600 rounded px-3 py-2 w-full mb-4"
          )}
          placeholder={`Enter ${label.toLowerCase()} name`}
          value={value}
          onChange={(e) => handleInputChange(e.target.value)}
          maxLength={MAX_LENGTHS.beltName}
        />
        {validationError && (
          <p className="text-red-600 dark:text-red-400 text-sm mb-4">{validationError}</p>
        )}
        <div className="flex justify-end gap-2">
          <button
            className="px-4 py-2 rounded-md bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600 transition"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            className="px-4 py-2 rounded-md bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 hover:bg-gray-100 dark:hover:bg-gray-600 transition"
            onClick={handleSave}
          >
            Save Item
          </button>
        </div>
      </div>
    </div>
  );
}

function AddMembershipModal({
  open,
  onClose,
  onSave,
  label,
}: {
  open: boolean;
  onClose: () => void;
  onSave: (name: string, price: number) => void;
  label: string;
}) {
  const [value, setValue] = useState("");
  const [price, setPrice] = useState("");
  const [validationError, setValidationError] = useState("");
  
  useEffect(() => {
    if (open) {
      setValue("");
      setPrice("");
      setValidationError("");
    }
  }, [open]);
  
  const handleNameChange = (inputValue: string) => {
    // Sanitize and validate input
    const sanitized = sanitizeInput(inputValue, 'membershipName');
    
    // Check length limit
    if (sanitized.length > MAX_LENGTHS.membershipName) {
      setValidationError(`Name must be ${MAX_LENGTHS.membershipName} characters or less`);
      return;
    }
    
    // Validate input
    if (sanitized && !validateInput(sanitized, 'membershipName')) {
      setValidationError("Invalid characters in name");
      return;
    }
    
    setValidationError("");
    setValue(sanitized);
  };
  
  const handlePriceChange = (inputValue: string) => {
    // Sanitize price input
    const sanitized = sanitizeInput(inputValue, 'membershipPrice');
    
    // Check length limit
    if (sanitized.length > MAX_LENGTHS.membershipPrice) {
      setValidationError("Price is too long");
      return;
    }
    
    // Validate price format
    if (sanitized && !validateInput(sanitized, 'membershipPrice')) {
      setValidationError("Invalid price format");
      return;
    }
    
    setValidationError("");
    setPrice(sanitized);
  };
  
  const handleSave = () => {
    if (!value.trim()) {
      setValidationError("Name is required");
      return;
    }
    
    if (!price.trim()) {
      setValidationError("Price is required");
      return;
    }
    
    if (!validateInput(value, 'membershipName')) {
      setValidationError("Invalid name format");
      logSecurityEvent('INVALID_INPUT_ATTEMPT', { field: 'membershipName', value: value.substring(0, 50) });
      return;
    }
    
    if (!validateInput(price, 'membershipPrice')) {
      setValidationError("Invalid price format");
      logSecurityEvent('INVALID_INPUT_ATTEMPT', { field: 'membershipPrice', value: price });
      return;
    }
    
    const priceNumber = Number(price);
    if (isNaN(priceNumber) || priceNumber < 0) {
      setValidationError("Price must be a positive number");
      return;
    }
    
    onSave(value.trim(), priceNumber);
    onClose();
  };
  
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 w-full max-w-sm">
        <h3 className="text-lg font-medium mb-4 text-gray-900 dark:text-white">
          Add {label}
        </h3>
        <input
          className={getInputClassName(
            "border border-gray-300 dark:border-gray-600 rounded px-3 py-2 w-full mb-4"
          )}
          placeholder={`Enter ${label.toLowerCase()} name`}
          value={value}
          onChange={(e) => handleNameChange(e.target.value)}
          maxLength={MAX_LENGTHS.membershipName}
        />
        <input
          type="number"
          step="0.01"
          min="0"
          className={getInputClassName(
            "border border-gray-300 dark:border-gray-600 rounded px-3 py-2 w-full mb-4"
          )}
          placeholder="E.g. 100.00"
          value={price}
          onChange={(e) => handlePriceChange(e.target.value)}
          maxLength={MAX_LENGTHS.membershipPrice}
        />
        {validationError && (
          <p className="text-red-600 dark:text-red-400 text-sm mb-4">{validationError}</p>
        )}
        <div className="flex justify-end gap-2">
          <button
            className="px-4 py-2 rounded-md bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600 transition"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            className="px-4 py-2 rounded-md bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 hover:bg-gray-100 dark:hover:bg-gray-600 transition"
            onClick={handleSave}
          >
            Save Item
          </button>
        </div>
      </div>
    </div>
  );
}

// Utility function for consistent input styling
const getInputClassName = (baseClasses: string) => {
  return `${baseClasses} bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-indigo-500 focus:border-indigo-500 dark:focus:ring-indigo-400 dark:focus:border-indigo-400`;
};

const CURRICULUM_PDF_MAX_BYTES = 15 * 1024 * 1024;
const CURRICULUM_PDF_ACCEPT = ".pdf,application/pdf";

function isCurriculumPdfFile(file: File): boolean {
  if (file.type === "application/pdf") return true;
  return /\.pdf$/i.test(file.name);
}

/** Aliases kept so older references (or merge drift) still resolve to the PDF-only helpers. */
const isCurriculumDocType = isCurriculumPdfFile;
const CURRICULUM_DOC_MAX_BYTES = CURRICULUM_PDF_MAX_BYTES;

/** Re-save PDF with pdf-lib (object streams) to reduce size when possible. Falls back to original on failure. */
async function compressCurriculumPdf(file: File): Promise<File> {
  if (!isCurriculumPdfFile(file)) return file;
  try {
    const { PDFDocument } = await import("pdf-lib");
    const bytes = new Uint8Array(await file.arrayBuffer());
    const pdfDoc = await PDFDocument.load(bytes);
    const out = await pdfDoc.save({ useObjectStreams: true });
    if (out.byteLength >= bytes.byteLength) {
      return file;
    }
    return new File([out], file.name, { type: "application/pdf" });
  } catch {
    return file;
  }
}

function readFileAsBase64Payload(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const base64Data = result.split(",")[1];
      resolve(base64Data);
    };
    reader.onerror = () => reject(new Error("Failed to read file"));
    reader.readAsDataURL(file);
  });
}

async function uploadCurriculumAttachmentFile(
  file: File,
  authToken: string,
  studioId: string
): Promise<{ url: string }> {
  const prepared = await compressCurriculumPdf(file);
  if (prepared.size > CURRICULUM_PDF_MAX_BYTES) {
    throw new Error("PDF must be 15MB or smaller (after compression)");
  }
  const base64Data = await readFileAsBase64Payload(prepared);
  const response = await fetch(
    `${process.env.REACT_APP_BACKEND_URL}/api/supabase/upload/file`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${authToken}`,
      },
      body: JSON.stringify({
        fileData: base64Data,
        fileName: prepared.name,
        fileType: "application/pdf",
        studioId: studioId || null,
      }),
    }
  );
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(
      (err as { error?: string }).error || "Failed to upload file"
    );
  }
  const uploadResult = await response.json();
  return { url: uploadResult.fileUrl };
}

// Sortable Curriculum Item Component
function SortableCurriculumItem({ 
  item, 
  curriculumEditId, 
  curriculumEditTitle,
  curriculumEditDescription,
  setCurriculumEditTitle,
  setCurriculumEditDescription,
  curriculumEditAttachmentUrl,
  curriculumEditAttachmentFilename,
  curriculumEditNewFile,
  setCurriculumEditNewFile,
  curriculumEditClearAttachment,
  setCurriculumEditClearAttachment,
  handleSaveCurriculumEdit, 
  cancelCurriculumEdit,
  beginCurriculumEdit,
  handleDeleteCurriculum 
}: {
  item: any;
  curriculumEditId: string | null;
  curriculumEditTitle: string;
  curriculumEditDescription: string;
  setCurriculumEditTitle: (value: string) => void;
  setCurriculumEditDescription: (value: string) => void;
  curriculumEditAttachmentUrl: string | null;
  curriculumEditAttachmentFilename: string | null;
  curriculumEditNewFile: File | null;
  setCurriculumEditNewFile: (file: File | null) => void;
  curriculumEditClearAttachment: boolean;
  setCurriculumEditClearAttachment: (value: boolean) => void;
  handleSaveCurriculumEdit: () => void;
  cancelCurriculumEdit: () => void;
  beginCurriculumEdit: (item: any) => void;
  handleDeleteCurriculum: (id: string) => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <li
      ref={setNodeRef}
      style={style}
      className="flex items-start py-2 pl-8 border-l-2 border-gray-200 dark:border-gray-600 ml-8"
    >
      {curriculumEditId === item.id ? (
        <div className="flex flex-col w-full gap-2">
          <input
            value={curriculumEditTitle}
            onChange={(e) => setCurriculumEditTitle(e.target.value)}
            placeholder="e.g. White Stripe Requirement"
            className={getInputClassName(
              "border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-sm"
            )}
            maxLength={MAX_LENGTHS.curriculumTitle}
          />
          <div className="flex flex-col lg:flex-row gap-3 lg:items-start">
            <div className="flex-1 flex flex-col gap-1 min-w-0">
              <label className="text-xs font-medium text-gray-600 dark:text-gray-400">
                Required curriculum (what students must demonstrate for this stripe)
              </label>
              <textarea
                value={curriculumEditDescription}
                onChange={(e) => setCurriculumEditDescription(e.target.value)}
                placeholder="Describe techniques, standards, or knowledge required. You can paste links (URLs). Optional."
                className={getInputClassName(
                  "border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-sm resize-none"
                )}
                rows={3}
                maxLength={MAX_LENGTHS.curriculumDescription}
              />
            </div>
            <div className="flex-1 lg:max-w-xs flex flex-col gap-1 min-w-0">
              <label className="text-xs font-medium text-gray-600 dark:text-gray-400">
                Supporting PDF
              </label>
              <p className="text-xs text-gray-500 dark:text-gray-500">
                PDF only, up to 15MB. Compressed when possible before upload.
              </p>
              <input
                type="file"
                accept={CURRICULUM_PDF_ACCEPT}
                className="text-xs text-gray-700 dark:text-gray-300 file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:text-xs file:bg-indigo-50 file:text-indigo-700 dark:file:bg-gray-600 dark:file:text-gray-200"
                onChange={(e) => {
                  const f = e.target.files?.[0] ?? null;
                  setCurriculumEditNewFile(f);
                  if (f) setCurriculumEditClearAttachment(false);
                }}
              />
              {curriculumEditClearAttachment && !curriculumEditNewFile && (
                <p className="text-xs text-amber-600 dark:text-amber-400">
                  Attachment will be removed when you save.
                </p>
              )}
              {!curriculumEditClearAttachment && curriculumEditNewFile && (
                <p className="text-xs text-gray-600 dark:text-gray-400 truncate">
                  New file: {curriculumEditNewFile.name}
                </p>
              )}
              {!curriculumEditClearAttachment && !curriculumEditNewFile && curriculumEditAttachmentUrl && (
                <div className="flex flex-wrap items-center gap-2">
                  <a
                    href={curriculumEditAttachmentUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-xs text-indigo-600 dark:text-indigo-400 hover:underline"
                  >
                    <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5 flex-shrink-0" />
                    {DOMPurify.sanitize(curriculumEditAttachmentFilename || "Current file", {
                      ALLOWED_TAGS: [],
                      ALLOWED_ATTR: [],
                    })}
                  </a>
                  <button
                    type="button"
                    className="text-xs text-red-600 dark:text-red-400 hover:underline"
                    onClick={() => {
                      setCurriculumEditClearAttachment(true);
                      setCurriculumEditNewFile(null);
                    }}
                  >
                    Remove
                  </button>
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              className="text-green-600 dark:text-green-400 p-1"
              onClick={handleSaveCurriculumEdit}
            >
              <CheckIcon className="w-5 h-5" />
            </button>
            <button
              className="text-gray-400 dark:text-gray-500 p-1"
              onClick={cancelCurriculumEdit}
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>
        </div>
      ) : (
        <div className="flex w-full items-start justify-between gap-2">
          <div className="flex items-start gap-2 flex-1">
            <div
              {...attributes}
              {...listeners}
              className="cursor-grab active:cursor-grabbing p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded mt-0.5"
            >
              <Bars3Icon className="w-4 h-4 text-gray-400" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 dark:text-white">
                {DOMPurify.sanitize(item.title, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] })}
              </p>
              {item.description && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {DOMPurify.sanitize(item.description, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] })}
                </p>
              )}
              {item.attachment_url && (
                <a
                  href={item.attachment_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-indigo-600 dark:text-indigo-400 hover:underline mt-1"
                >
                  <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5 flex-shrink-0" />
                  {DOMPurify.sanitize(item.attachment_filename || "Attachment", {
                    ALLOWED_TAGS: [],
                    ALLOWED_ATTR: [],
                  })}
                </a>
              )}
            </div>
          </div>
          <div className="flex gap-1 flex-shrink-0">
            <button
              className="p-1.5 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-100 dark:hover:bg-gray-600 transition text-blue-600 dark:text-blue-400"
              onClick={() => beginCurriculumEdit(item)}
            >
              <PencilIcon className="w-4 h-4" />
            </button>
            <button
              className="p-1.5 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-100 dark:hover:bg-gray-600 transition text-red-600 dark:text-red-400"
              onClick={() => handleDeleteCurriculum(item.id)}
            >
              <TrashIcon className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </li>
  );
}

// Sortable Belt Item Component
function SortableBeltItem({ 
  belt, 
  index, 
  beltEditId, 
  beltEditValue, 
  setBeltEditValue, 
  handleSaveBeltEdit, 
  setBeltEditId, 
  handleDeleteBelt,
  expandedBelts,
  toggleBeltExpansion,
  curriculum,
  curriculumLoading,
  curriculumEditId,
  curriculumEditTitle,
  curriculumEditDescription,
  setCurriculumEditTitle,
  setCurriculumEditDescription,
  handleSaveCurriculumEdit,
  handleDeleteCurriculum,
  handleAddCurriculum,
  handleCurriculumDragEnd,
  sensors,
  addingCurriculumBeltId,
  newCurriculumTitle,
  newCurriculumDescription,
  newCurriculumFile,
  setNewCurriculumFile,
  setNewCurriculumTitle,
  setNewCurriculumDescription,
  handleSaveNewCurriculum,
  handleCancelAddCurriculum,
  curriculumEditAttachmentUrl,
  curriculumEditAttachmentFilename,
  curriculumEditNewFile,
  setCurriculumEditNewFile,
  curriculumEditClearAttachment,
  setCurriculumEditClearAttachment,
  cancelCurriculumEdit,
  beginCurriculumEdit,
}: {
  belt: any;
  index: number;
  beltEditId: string | null;
  beltEditValue: string;
  setBeltEditValue: (value: string) => void;
  handleSaveBeltEdit: (id: string, name: string) => void;
  setBeltEditId: (id: string | null) => void;
  handleDeleteBelt: (id: string) => void;
  expandedBelts: Set<string>;
  toggleBeltExpansion: (beltId: string) => void;
  curriculum: Record<string, any[]>;
  curriculumLoading: Record<string, boolean>;
  curriculumEditId: string | null;
  curriculumEditTitle: string;
  curriculumEditDescription: string;
  setCurriculumEditTitle: (value: string) => void;
  setCurriculumEditDescription: (value: string) => void;
  handleSaveCurriculumEdit: () => void;
  handleDeleteCurriculum: (id: string) => void;
  handleAddCurriculum: (beltId: string) => void;
  handleCurriculumDragEnd: (event: DragEndEvent, beltId: string) => void;
  sensors: any;
  addingCurriculumBeltId: string | null;
  newCurriculumTitle: string;
  newCurriculumDescription: string;
  newCurriculumFile: File | null;
  setNewCurriculumFile: (file: File | null) => void;
  setNewCurriculumTitle: (value: string) => void;
  setNewCurriculumDescription: (value: string) => void;
  handleSaveNewCurriculum: (beltId: string) => void;
  handleCancelAddCurriculum: () => void;
  curriculumEditAttachmentUrl: string | null;
  curriculumEditAttachmentFilename: string | null;
  curriculumEditNewFile: File | null;
  setCurriculumEditNewFile: (file: File | null) => void;
  curriculumEditClearAttachment: boolean;
  setCurriculumEditClearAttachment: (value: boolean) => void;
  cancelCurriculumEdit: () => void;
  beginCurriculumEdit: (item: any) => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: belt.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const isExpanded = expandedBelts.has(belt.id);
  const beltCurriculum = curriculum[belt.id] || [];
  const isLoadingCurriculum = curriculumLoading[belt.id];

  return (
    <li
      ref={setNodeRef}
      style={style}
      className="flex flex-col py-2"
    >
      <div className="flex items-center justify-between">
        {beltEditId === belt.id ? (
          <div className="flex flex-col xl:flex-row xl:items-center w-full gap-2">
            <input
              value={beltEditValue}
              onChange={(e) => setBeltEditValue(e.target.value)}
              className={getInputClassName(
                "border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-sm flex-1"
              )}
              maxLength={MAX_LENGTHS.beltName}
            />
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                className="text-green-600 dark:text-green-400"
                onClick={() => handleSaveBeltEdit(belt.id, beltEditValue)}
              >
                <CheckIcon className="w-5 h-5" />
              </button>
              <button
                className="text-gray-400 dark:text-gray-500"
                onClick={() => setBeltEditId(null)}
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>
          </div>
        ) : (
          <div className="flex w-full items-center justify-between">
            <div className="flex items-center gap-3">
              <div
                {...attributes}
                {...listeners}
                className="cursor-grab active:cursor-grabbing p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
              >
                <Bars3Icon className="w-4 h-4 text-gray-400" />
              </div>
              <span className="text-sm text-gray-500 dark:text-gray-400 w-8">
                {belt.order || index + 1}
              </span>
              <span className="text-gray-900 dark:text-white">
                {DOMPurify.sanitize(belt.name, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] })}
              </span>
              <button
                onClick={() => toggleBeltExpansion(belt.id)}
                className="ml-2 p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
              >
                {isExpanded ? (
                  <ChevronDownIcon className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                ) : (
                  <ChevronRightIcon className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                )}
              </button>
            </div>
            <div className="flex gap-2 ml-2">
              <button
                className="p-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-100 dark:hover:bg-gray-600 transition text-blue-600 dark:text-blue-400"
                onClick={() => {
                  setBeltEditId(belt.id);
                  setBeltEditValue(belt.name);
                }}
              >
                <PencilIcon className="w-5 h-5" />
              </button>
              <button
                className="p-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-100 dark:hover:bg-gray-600 transition text-red-600 dark:text-red-400"
                onClick={() => handleDeleteBelt(belt.id)}
              >
                <TrashIcon className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}
      </div>
      
      {/* Curriculum Section */}
      {isExpanded && (
        <div className="mt-3 ml-6 pl-4 border-l-2 border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Curriculum</h4>
            {addingCurriculumBeltId !== belt.id && (
              <button
                className="flex items-center gap-1 px-2 py-1 text-xs bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-100 dark:hover:bg-gray-600 transition text-green-600 dark:text-green-400"
                onClick={() => handleAddCurriculum(belt.id)}
              >
                <PlusIcon className="w-3 h-3" /> Add Item
              </button>
            )}
          </div>
          
          {/* Inline Add Form */}
          {addingCurriculumBeltId === belt.id && (
            <div className="mb-3 pl-8 border-l-2 border-gray-200 dark:border-gray-600 ml-8">
              <div className="flex flex-col gap-2">
                <input
                  value={newCurriculumTitle}
                  onChange={(e) => {
                    const sanitizedValue = sanitizeInput(e.target.value, 'curriculumTitle');
                    if (sanitizedValue.length <= MAX_LENGTHS.curriculumTitle) {
                      setNewCurriculumTitle(sanitizedValue);
                    }
                  }}
                  placeholder="e.g. White Stripe Requirement"
                  className={getInputClassName(
                    "border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-sm"
                  )}
                  maxLength={MAX_LENGTHS.curriculumTitle}
                  autoFocus
                />
                <div className="flex flex-col lg:flex-row gap-3 lg:items-start">
                  <div className="flex-1 flex flex-col gap-1 min-w-0">
                    <label className="text-xs font-medium text-gray-600 dark:text-gray-400">
                      Required curriculum (what students must demonstrate for this stripe)
                    </label>
                    <textarea
                      value={newCurriculumDescription}
                      onChange={(e) => {
                        const sanitizedValue = sanitizeInput(e.target.value, 'curriculumDescription');
                        if (sanitizedValue.length <= MAX_LENGTHS.curriculumDescription) {
                          setNewCurriculumDescription(sanitizedValue);
                        }
                      }}
                      placeholder="Describe techniques, standards, or knowledge required. You can paste links (URLs). Optional."
                      className={getInputClassName(
                        "border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-sm resize-none"
                      )}
                      rows={3}
                      maxLength={MAX_LENGTHS.curriculumDescription}
                    />
                  </div>
                  <div className="flex-1 lg:max-w-xs flex flex-col gap-1 min-w-0">
                    <label className="text-xs font-medium text-gray-600 dark:text-gray-400">
                      Supporting PDF
                    </label>
                    <p className="text-xs text-gray-500 dark:text-gray-500">
                      PDF only, up to 15MB. Compressed when possible before upload.
                    </p>
                    <input
                      type="file"
                      accept={CURRICULUM_PDF_ACCEPT}
                      className="text-xs text-gray-700 dark:text-gray-300 file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:text-xs file:bg-indigo-50 file:text-indigo-700 dark:file:bg-gray-600 dark:file:text-gray-200"
                      onChange={(e) => {
                        setNewCurriculumFile(e.target.files?.[0] ?? null);
                      }}
                    />
                    {newCurriculumFile && (
                      <p className="text-xs text-gray-600 dark:text-gray-400 truncate">
                        {newCurriculumFile.name}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    className="text-green-600 dark:text-green-400 p-1"
                    onClick={() => handleSaveNewCurriculum(belt.id)}
                  >
                    <CheckIcon className="w-5 h-5" />
                  </button>
                  <button
                    className="text-gray-400 dark:text-gray-500 p-1"
                    onClick={handleCancelAddCurriculum}
                  >
                    <XMarkIcon className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          )}
          
          {isLoadingCurriculum ? (
            <div className="text-sm text-gray-500 dark:text-gray-400 py-2">Loading curriculum...</div>
          ) : beltCurriculum.length === 0 && addingCurriculumBeltId !== belt.id ? (
            <div className="text-sm text-gray-500 dark:text-gray-400 py-2">No curriculum items yet. Click "Add Item" to get started.</div>
          ) : beltCurriculum.length > 0 && (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={(event) => handleCurriculumDragEnd(event, belt.id)}
            >
              <SortableContext
                items={beltCurriculum.map(item => item.id)}
                strategy={verticalListSortingStrategy}
              >
                <ul className="space-y-2">
                  {beltCurriculum.map((item: any) => (
                    <SortableCurriculumItem
                      key={item.id}
                      item={item}
                      curriculumEditId={curriculumEditId}
                      curriculumEditTitle={curriculumEditTitle}
                      curriculumEditDescription={curriculumEditDescription}
                      setCurriculumEditTitle={setCurriculumEditTitle}
                      setCurriculumEditDescription={setCurriculumEditDescription}
                      curriculumEditAttachmentUrl={curriculumEditAttachmentUrl}
                      curriculumEditAttachmentFilename={curriculumEditAttachmentFilename}
                      curriculumEditNewFile={curriculumEditNewFile}
                      setCurriculumEditNewFile={setCurriculumEditNewFile}
                      curriculumEditClearAttachment={curriculumEditClearAttachment}
                      setCurriculumEditClearAttachment={setCurriculumEditClearAttachment}
                      handleSaveCurriculumEdit={handleSaveCurriculumEdit}
                      cancelCurriculumEdit={cancelCurriculumEdit}
                      beginCurriculumEdit={beginCurriculumEdit}
                      handleDeleteCurriculum={handleDeleteCurriculum}
                    />
                  ))}
                </ul>
              </SortableContext>
            </DndContext>
          )}
        </div>
      )}
    </li>
  );
}

function SettingsPlanLoadingSection({ title }: { title: string }) {
  return (
    <section className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 w-full">
      <h2 className="text-lg font-medium leading-6 text-gray-900 dark:text-white mb-4">
        {title}
      </h2>
      <div className="flex justify-center items-center py-8">
        <div className="animate-pulse flex space-x-4 items-center">
          <div className="h-5 w-5 bg-indigo-200 dark:bg-indigo-600 rounded-full animate-spin" />
          <div className="text-sm text-gray-600 dark:text-gray-400">Loading...</div>
        </div>
      </div>
    </section>
  );
}

export default function GeneralSettings() {
  const { studioUser } = useStudioUser();
  const { getAuthToken } = useAuth();
  const { hasCapability, loading: planLoading, plan } = useStudioPlan();
  const canManageStripeMemberships = !planLoading && hasCapability('stripeBilling');
  const canSendSms = hasCapability('messagingSms');
  const canManageSubAccounts =
    !planLoading && (plan?.capabilities.maxSubAccounts ?? 0) > 0;
  const showStaffLoginUrl = canManageSubAccounts;
  
  // Security-related state
  const [csrfToken, setCsrfToken] = useState<string>('');
  const [lastSubmissionTime, setLastSubmissionTime] = useState<number>(0);
  
  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );
  const [notification, setNotification] = useState<{
    show: boolean;
    message: string;
    title?: string;
  }>({
    show: false,
    message: "",
  });
  const [isStripeConnected, setIsStripeConnected] = useState(false);
  const [stripeLoading, setStripeLoading] = useState(true);
  const [studioCode, setStudioCode] = useState<string>("");
  const [autoSmsMessage, setAutoSmsMessage] = useState<string>("");
  const [studioCodeInfo, setStudioCodeInfo] = useState<{
    lastChanged: string;
    expiresAt: string;
    previousCode?: string;
    daysUntilExpiration: number;
    needsRotation: boolean;
  } | null>(null);
  const [rotatingCode, setRotatingCode] = useState(false);

  // Belts
  const [belts, setBelts] = useState<any[]>([]);
  const [beltsLoading, setBeltsLoading] = useState(true);
  const [beltEditId, setBeltEditId] = useState<string | null>(null);
  const [beltEditValue, setBeltEditValue] = useState("");
  const [showBeltModal, setShowBeltModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<string | null>(null);
  const [deleteType, setDeleteType] = useState<string | null>(null);

  // Curriculum
  const [curriculum, setCurriculum] = useState<Record<string, any[]>>({});
  const [curriculumLoading, setCurriculumLoading] = useState<Record<string, boolean>>({});
  const [curriculumEditId, setCurriculumEditId] = useState<string | null>(null);
  const [curriculumEditTitle, setCurriculumEditTitle] = useState("");
  const [curriculumEditDescription, setCurriculumEditDescription] = useState("");
  const [expandedBelts, setExpandedBelts] = useState<Set<string>>(new Set());
  const [addingCurriculumBeltId, setAddingCurriculumBeltId] = useState<string | null>(null);
  const [newCurriculumTitle, setNewCurriculumTitle] = useState("");
  const [newCurriculumDescription, setNewCurriculumDescription] = useState("");
  const [newCurriculumFile, setNewCurriculumFile] = useState<File | null>(null);
  const [curriculumEditAttachmentUrl, setCurriculumEditAttachmentUrl] = useState<
    string | null
  >(null);
  const [curriculumEditAttachmentFilename, setCurriculumEditAttachmentFilename] =
    useState<string | null>(null);
  const [curriculumEditNewFile, setCurriculumEditNewFile] = useState<File | null>(
    null
  );
  const [curriculumEditClearAttachment, setCurriculumEditClearAttachment] =
    useState(false);

  // Lead Visibility
  const [leadVisibility, setLeadVisibility] = useState<boolean>(true);
  const [leadVisibilityLoading, setLeadVisibilityLoading] = useState(true);

  // Auto Appointment SMS
  const [autoAppointmentSms, setAutoAppointmentSms] = useState<boolean>(true);
  const [autoAppointmentSmsLoading, setAutoAppointmentSmsLoading] = useState(true);
  const [autoSmsMessageLoading, setAutoSmsMessageLoading] = useState(false);
  
  // Follow-up SMS Messages
  const [followUpSmsMessage2, setFollowUpSmsMessage2] = useState<string>("");
  const [followUpSmsMessage3, setFollowUpSmsMessage3] = useState<string>("");
  const [followUpSmsMessage2Loading, setFollowUpSmsMessage2Loading] = useState(false);
  const [followUpSmsMessage3Loading, setFollowUpSmsMessage3Loading] = useState(false);
  
  // Missed Appointment SMS Message
  const [missedAppointmentSmsMessage, setMissedAppointmentSmsMessage] = useState<string>("");
  const [missedAppointmentSmsMessageLoading, setMissedAppointmentSmsMessageLoading] = useState(false);

  // SMS Configuration
  const [smsConfig, setSmsConfig] = useState<{
    isConfigured: boolean;
    provider: string;
  }>({
    isConfigured: false,
    provider: "twilio",
  });
  const [smsConfigLoading, setSmsConfigLoading] = useState(true);

  // Memberships
  const [memberships, setMemberships] = useState<any[]>([]);
  const [membershipsLoading, setMembershipsLoading] = useState(true);
  const [membershipEditId, setMembershipEditId] = useState<string | null>(null);
  const [membershipEditValue, setMembershipEditValue] = useState("");
  const [membershipEditPrice, setMembershipEditPrice] = useState("");
  const [showMembershipModal, setShowMembershipModal] = useState(false);

  // Staff Roles
  const [staffRoles, setStaffRoles] = useState<any[]>([]);
  const [staffRolesLoading, setStaffRolesLoading] = useState(true);
  const [roleEditId, setRoleEditId] = useState<string | null>(null);
  const [roleEditValue, setRoleEditValue] = useState("");
  const [showRoleModal, setShowRoleModal] = useState(false);

  // One-time payment descriptions (API: product-descriptions)
  const [productDescriptions, setProductDescriptions] = useState<any[]>([]);
  const [productDescriptionsLoading, setProductDescriptionsLoading] = useState(true);
  const [descriptionEditId, setDescriptionEditId] = useState<string | null>(
    null
  );
  const [descriptionEditValue, setDescriptionEditValue] = useState("");
  const [showDescriptionModal, setShowDescriptionModal] = useState(false);

  // AI Drop-off Risk
  const [highRiskThreshold, setHighRiskThreshold] = useState(40);
  const [riskThresholdLoading, setRiskThresholdLoading] = useState(true);
  const [dropoutSmsMessage, setDropoutSmsMessage] = useState("");
  const [dropoutEmailMessage, setDropoutEmailMessage] = useState("");
  const [sendDropoutSms, setSendDropoutSms] = useState(false);
  const [sendDropoutEmail, setSendDropoutEmail] = useState(false);

  // Add state for sub-accounts and limit
  const SUBACCOUNT_LIMIT = 5;
  const STAFF_ROLES_LIMIT = 10;
  const PRODUCT_DESCRIPTIONS_LIMIT = 15;
  const [subAccounts, setSubAccounts] = useState<any[]>([]);
  const subAccountLimitReached = subAccounts.length >= SUBACCOUNT_LIMIT;
  const staffRolesLimitReached = staffRoles.length >= STAFF_ROLES_LIMIT;
  const productDescriptionsLimitReached = productDescriptions.length >= PRODUCT_DESCRIPTIONS_LIMIT;

  // Fetch CSRF token
  const fetchCsrfToken = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/supabase/csrf-token`);
      if (response.ok) {
        const data = await response.json();
        if (data.token) {
          setCsrfToken(data.token);
        } else {
          logSecurityEvent('CSRF_TOKEN_INVALID_RESPONSE', { data });
        }
      } else {
        logSecurityEvent('CSRF_TOKEN_FETCH_ERROR', { status: response.status, statusText: response.statusText });
      }
    } catch (error) {
      logSecurityEvent('CSRF_TOKEN_FETCH_FAILED', { error: error instanceof Error ? error.message : 'Unknown error' });
      // In development, generate a fallback token
      if (process.env.NODE_ENV !== 'production') {
        const fallbackToken = Math.random().toString(36).substring(2) + Date.now().toString(36);
        setCsrfToken(fallbackToken);
        console.warn('Using fallback CSRF token in development mode');
      }
    }
  };

  // Fetch data for each section
  useEffect(() => {
    const authToken = getAuthToken();
    if (!studioUser?.studio_id || !authToken) return;

    // Fetch CSRF token first
    fetchCsrfToken();

    // Fetch Studio Info (including studio_code)
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/supabase/studios/me`, {
      headers: { Authorization: `Bearer ${authToken}` },
    })
      .then((res) => res.json())
      .then((data) => {
        setStudioCode(data.studio_code || "");
        setAutoSmsMessage(data.auto_appointment_sms_message || "Hi {name}! Thanks for your interest in {studio_name}. We'd love to have you visit us for a class - whether you want to participate, ask questions, or just observe to see if it's right for you. Here are some available times: {appointment_link}");
        setFollowUpSmsMessage2(data.follow_up_sms_message_2 || "");
        setFollowUpSmsMessage3(data.follow_up_sms_message_3 || "");
        setMissedAppointmentSmsMessage(data.missed_appointment_sms_message || "");
        // Fetch additional code info
        return fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/supabase/studios/${data.id}/code-info`,
          {
            headers: { Authorization: `Bearer ${authToken}` },
          }
        );
      })
      .then((res) => res.json())
      .then((codeInfo) => {
        setStudioCodeInfo(codeInfo);
      })
      .catch((error) => {
        // Error handling without console logging
      });

    // Fetch Belts
    setBeltsLoading(true);
    fetch(
      `${process.env.REACT_APP_BACKEND_URL}/api/supabase/belts/${studioUser.studio_id}`,
      {
        headers: { Authorization: `Bearer ${authToken}` },
      }
    )
      .then((res) => res.json())
      .then((data) => {
        setBelts(data);
        setBeltsLoading(false);
      })
      .catch((error) => {
        setBeltsLoading(false);
      });
    // Fetch Memberships
    setMembershipsLoading(true);
    fetch(
      `${process.env.REACT_APP_BACKEND_URL}/api/supabase/memberships/${studioUser.studio_id}`,
      {
        headers: { Authorization: `Bearer ${authToken}` },
      }
    )
      .then((res) => res.json())
      .then((data) => {
        setMemberships(data);
        setMembershipsLoading(false);
      })
      .catch((error) => {
        setMembershipsLoading(false);
      });
    // Fetch Staff Roles
    setStaffRolesLoading(true);
    fetch(
      `${process.env.REACT_APP_BACKEND_URL}/api/supabase/staff-roles/${studioUser.studio_id}`,
      {
        headers: { Authorization: `Bearer ${authToken}` },
      }
    )
      .then((res) => res.json())
      .then((data) => {
        setStaffRoles(data);
        setStaffRolesLoading(false);
      })
      .catch((error) => {
        setStaffRolesLoading(false);
      });
    // Fetch one-time payment descriptions
    setProductDescriptionsLoading(true);
    fetch(
      `${process.env.REACT_APP_BACKEND_URL}/api/supabase/product-descriptions/${studioUser.studio_id}`,
      {
        headers: { Authorization: `Bearer ${authToken}` },
      }
    )
      .then((res) => res.json())
      .then((data) => {
        setProductDescriptions(data);
        setProductDescriptionsLoading(false);
      })
      .catch((error) => {
        setProductDescriptionsLoading(false);
      });
    // Fetch Lead Visibility
    setLeadVisibilityLoading(true);
    fetch(
      `${process.env.REACT_APP_BACKEND_URL}/api/supabase/lead-visibility/${studioUser.studio_id}`,
      {
        headers: { Authorization: `Bearer ${authToken}` },
      }
    )
      .then((res) => res.json())
      .then((data) => {
        setLeadVisibility(data?.is_enabled ?? true);
        setLeadVisibilityLoading(false);
      })
      .catch((error) => {
        setLeadVisibilityLoading(false);
      });

    // Fetch Auto Appointment SMS setting
    setAutoAppointmentSmsLoading(true);
    fetch(
      `${process.env.REACT_APP_BACKEND_URL}/api/supabase/studios/me`,
      {
        headers: { Authorization: `Bearer ${authToken}` },
      }
    )
      .then((res) => res.json())
      .then((data) => {
        setAutoAppointmentSms(data?.auto_appointment_sms_enabled ?? true);
        setAutoSmsMessage(data?.auto_appointment_sms_message || "Hi {name}! Thanks for your interest in {studio_name}. We'd love to have you visit us for a class - whether you want to participate, ask questions, or just observe to see if it's right for you. Here are some available times: {appointment_link}");
        setFollowUpSmsMessage2(data?.follow_up_sms_message_2 || "");
        setFollowUpSmsMessage3(data?.follow_up_sms_message_3 || "");
        setAutoAppointmentSmsLoading(false);
      })
      .catch((error) => {
        setAutoAppointmentSmsLoading(false);
      });

    // Fetch SMS Configuration
    setSmsConfigLoading(true);
    fetch(
      `${process.env.REACT_APP_BACKEND_URL}/api/supabase/sms-config?studio_id=${studioUser.studio_id}`,
      {
        headers: { Authorization: `Bearer ${authToken}` },
      }
    )
      .then((res) => res.json())
      .then((config) => {
        // Check if SMS is configured for EITHER provider type
        const isConfigured = 
          (config.provider === 'studio_managed' && !!config.fromnumber) || // Studio-managed
          (config.provider === 'own_twilio' && !!config.accountsid) ||      // Own Twilio
          (!config.provider && !!config.accountsid);                        // Legacy (no provider field)
        
        setSmsConfig({
          isConfigured,
          provider: config.provider === 'studio_managed' ? 'studio_managed' : 'twilio',
        });
        setSmsConfigLoading(false);
      })
      .catch((error) => {
        setSmsConfig({
          isConfigured: false,
          provider: "twilio",
        });
        setSmsConfigLoading(false);
      });
    // Add Stripe connection check
    setStripeLoading(true);
    fetch(
      `${process.env.REACT_APP_BACKEND_URL}/api/supabase/studios/${studioUser.studio_id}`,
      {
        headers: { Authorization: `Bearer ${authToken}` },
      }
    )
      .then((res) => res.json())
      .then((data) => {
        setIsStripeConnected(!!data.stripe_user_id);
        setStripeLoading(false);
      });

    // Fetch AI Risk Thresholds
    setRiskThresholdLoading(true);
    fetch(
      `${process.env.REACT_APP_BACKEND_URL}/api/supabase/studios/${studioUser.studio_id}/risk-thresholds`,
      {
        headers: { Authorization: `Bearer ${authToken}` },
      }
    )
      .then((res) => {
        return res.json();
      })
      .then((data) => {
        if (data && typeof data.threshold === "number") {
          const initialValue = data.threshold * 100;
          setHighRiskThreshold(initialValue);
        }
        if (data?.sms_message !== undefined) {
          setDropoutSmsMessage(data.sms_message || "");
        }
        if (data?.email_message !== undefined) {
          setDropoutEmailMessage(data.email_message || "");
        }
        if (data?.send_sms !== undefined) {
          setSendDropoutSms(data.send_sms || false);
        }
        if (data?.send_email !== undefined) {
          setSendDropoutEmail(data.send_email || false);
        }
      })
      .catch((error) => {
        // Error handling without console logging
      })
      .finally(() => {
        setRiskThresholdLoading(false);
      });

    // Fetch sub-accounts
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/supabase/sub-accounts/${studioUser.studio_id}`, {
      headers: { Authorization: `Bearer ${authToken}` },
    })
      .then((res) => res.json())
      .then((data) => setSubAccounts(data || []));
  }, [studioUser?.studio_id, getAuthToken]);

  // Add handlers for each modal
  const handleAddBelt = async (name: string) => {
    // Rate limiting check
    const now = Date.now();
    if (now - lastSubmissionTime < SUBMISSION_COOLDOWN) {
      setNotification({
        show: true,
        message: "Please wait before submitting again",
        title: "Error",
      });
      return;
    }

    // CSRF token validation
    if (!csrfToken) {
      logSecurityEvent('CSRF_TOKEN_MISSING', { action: 'addBelt' });
      if (process.env.NODE_ENV === 'production') {
        setNotification({
          show: true,
          message: "Security token missing. Please refresh the page and try again.",
          title: "Error",
        });
        return;
      } else {
        console.warn('CSRF token missing in development mode - proceeding with submission');
      }
    }

    // Input validation
    if (!validateInput(name, 'beltName')) {
      logSecurityEvent('INVALID_INPUT_ATTEMPT', { field: 'beltName', value: name.substring(0, 50) });
      setNotification({
        show: true,
        message: "Invalid belt name format",
        title: "Error",
      });
      return;
    }

    const authToken = getAuthToken();
    if (!studioUser?.studio_id || !authToken) return;
    
    setLastSubmissionTime(now);
    
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/supabase/belts`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
            "X-CSRF-Token": csrfToken,
          },
          body: JSON.stringify({
            name: sanitizeInput(name, 'beltName'),
            studioId: studioUser.studio_id,
          }),
        }
      );
      if (response.ok) {
        const newBelt = await response.json();
        setBelts([...belts, newBelt]);
        setNotification({
          show: true,
          message: "Belt added successfully",
          title: "Success",
        });
      } else {
        throw new Error("Failed to add belt");
      }
    } catch (error) {
      logSecurityEvent('API_REQUEST_FAILED', { 
        action: 'addBelt', 
        error: error instanceof Error ? error.message : 'Unknown error' 
      });
      setNotification({
        show: true,
        message: error instanceof Error ? error.message : "Failed to add belt",
        title: "Error",
      });
    }
  };

  const handleReorderBelt = async (beltId: string, newOrder: number) => {
    const authToken = getAuthToken();
    if (!studioUser?.studio_id || !authToken) return;
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/supabase/belts/${studioUser.studio_id}/${beltId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
          },
          body: JSON.stringify({ order: newOrder }),
        }
      );
      if (response.ok) {
        const updatedBelt = await response.json();
        setBelts(belts.map(b => b.id === beltId ? updatedBelt : b));
        setNotification({
          show: true,
          message: "Belt order updated successfully",
          title: "Success",
        });
      } else {
        throw new Error("Failed to update belt order");
      }
    } catch (error) {
      setNotification({
        show: true,
        message: error instanceof Error ? error.message : "Failed to update belt order",
        title: "Error",
      });
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      const oldIndex = belts.findIndex(belt => belt.id === active.id);
      const newIndex = belts.findIndex(belt => belt.id === over?.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        // Update local state immediately for responsive UI with new order values
        const newBelts = arrayMove(belts, oldIndex, newIndex).map((belt, index) => ({
          ...belt,
          order: index + 1
        }));
        setBelts(newBelts);

        // Update the order values based on new positions
        const updatedBelts = newBelts;

        // Update all belts with their new order values
        const authToken = getAuthToken();
        if (studioUser?.studio_id && authToken) {
          try {
            const updatePromises = updatedBelts.map(belt => 
              fetch(
                `${process.env.REACT_APP_BACKEND_URL}/api/supabase/belts/${studioUser.studio_id}/${belt.id}`,
                {
                  method: "PUT",
                  headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${authToken}`,
                  },
                  body: JSON.stringify({ order: belt.order }),
                }
              )
            );

            await Promise.all(updatePromises);
            
            setNotification({
              show: true,
              message: "Belt order updated successfully",
              title: "Success",
            });
          } catch (error) {
            setNotification({
              show: true,
              message: "Failed to update belt order",
              title: "Error",
            });
            // Revert to original order if update fails
            setBelts(belts);
          }
        }
      }
    }
  };

  const handleAddMembership = async (name: string, price: number) => {
    // Rate limiting check
    const now = Date.now();
    if (now - lastSubmissionTime < SUBMISSION_COOLDOWN) {
      setNotification({
        show: true,
        message: "Please wait before submitting again",
        title: "Error",
      });
      return;
    }

    // CSRF token validation
    if (!csrfToken) {
      logSecurityEvent('CSRF_TOKEN_MISSING', { action: 'addMembership' });
      if (process.env.NODE_ENV === 'production') {
        setNotification({
          show: true,
          message: "Security token missing. Please refresh the page and try again.",
          title: "Error",
        });
        return;
      } else {
        console.warn('CSRF token missing in development mode - proceeding with submission');
      }
    }

    // Input validation
    if (!validateInput(name, 'membershipName')) {
      logSecurityEvent('INVALID_INPUT_ATTEMPT', { field: 'membershipName', value: name.substring(0, 50) });
      setNotification({
        show: true,
        message: "Invalid membership name format",
        title: "Error",
      });
      return;
    }

    if (!validateInput(price.toString(), 'membershipPrice')) {
      logSecurityEvent('INVALID_INPUT_ATTEMPT', { field: 'membershipPrice', value: price.toString() });
      setNotification({
        show: true,
        message: "Invalid price format",
        title: "Error",
      });
      return;
    }

    const authToken = getAuthToken();
    if (!studioUser?.studio_id || !authToken) return;
    
    setLastSubmissionTime(now);
    
    try {
      const sanitizedName = sanitizeInput(name, 'membershipName');
      const headers = {
        "Content-Type": "application/json",
        Authorization: `Bearer ${authToken}`,
        "X-CSRF-Token": csrfToken,
      };

      if (canManageStripeMemberships) {
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/stripe/memberships`,
          {
            method: "POST",
            headers,
            body: JSON.stringify({
              name: sanitizedName,
              price,
              studio_id: studioUser.studio_id,
            }),
          }
        );
        if (response.ok) {
          const newMembership = await response.json();
          setMemberships([...memberships, newMembership]);
          setNotification({
            show: true,
            message: "Membership added successfully",
            title: "Success",
          });
        } else {
          throw new Error("Failed to add membership");
        }
      } else {
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/supabase/memberships`,
          {
            method: "POST",
            headers,
            body: JSON.stringify({
              studioId: studioUser.studio_id,
              name: sanitizedName,
              price,
            }),
          }
        );
        if (response.ok) {
          const newMembership = await response.json();
          setMemberships([...memberships, newMembership]);
          setNotification({
            show: true,
            message: "Membership added successfully",
            title: "Success",
          });
        } else {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error || "Failed to add membership");
        }
      }
    } catch (error) {
      logSecurityEvent('API_REQUEST_FAILED', { 
        action: 'addMembership', 
        error: error instanceof Error ? error.message : 'Unknown error' 
      });
      setNotification({
        show: true,
        message:
          error instanceof Error ? error.message : "Failed to add membership",
        title: "Error",
      });
    }
  };

  const handleAddRole = async (name: string) => {
    // Rate limiting check
    const now = Date.now();
    if (now - lastSubmissionTime < SUBMISSION_COOLDOWN) {
      setNotification({
        show: true,
        message: "Please wait before submitting again",
        title: "Error",
      });
      return;
    }

    // CSRF token validation
    if (!csrfToken) {
      logSecurityEvent('CSRF_TOKEN_MISSING', { action: 'addRole' });
      if (process.env.NODE_ENV === 'production') {
        setNotification({
          show: true,
          message: "Security token missing. Please refresh the page and try again.",
          title: "Error",
        });
        return;
      } else {
        console.warn('CSRF token missing in development mode - proceeding with submission');
      }
    }

    // Input validation
    if (!validateInput(name, 'roleName')) {
      logSecurityEvent('INVALID_INPUT_ATTEMPT', { field: 'roleName', value: name.substring(0, 50) });
      setNotification({
        show: true,
        message: "Invalid role name format",
        title: "Error",
      });
      return;
    }

    const authToken = getAuthToken();
    if (!studioUser?.studio_id || !authToken) return;
    
    if (staffRolesLimitReached) {
      setNotification({
        show: true,
        message: `You can only create up to ${STAFF_ROLES_LIMIT} staff roles per studio.`,
        title: "Limit Reached",
      });
      return;
    }
    
    setLastSubmissionTime(now);
    
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/supabase/staff-roles`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
            "X-CSRF-Token": csrfToken,
          },
          body: JSON.stringify({
            name: sanitizeInput(name, 'roleName'),
            studioId: studioUser.studio_id,
          }),
        }
      );
      if (response.ok) {
        const newRole = await response.json();
        setStaffRoles([...staffRoles, newRole]);
        setNotification({
          show: true,
          message: "Role added successfully",
          title: "Success",
        });
      } else {
        throw new Error("Failed to add role");
      }
    } catch (error) {
      logSecurityEvent('API_REQUEST_FAILED', { 
        action: 'addRole', 
        error: error instanceof Error ? error.message : 'Unknown error' 
      });
      setNotification({
        show: true,
        message: error instanceof Error ? error.message : "Failed to add role",
        title: "Error",
      });
    }
  };

  const handleAddDescription = async (description: string) => {
    // Rate limiting check
    const now = Date.now();
    if (now - lastSubmissionTime < SUBMISSION_COOLDOWN) {
      setNotification({
        show: true,
        message: "Please wait before submitting again",
        title: "Error",
      });
      return;
    }

    // CSRF token validation
    if (!csrfToken) {
      logSecurityEvent('CSRF_TOKEN_MISSING', { action: 'addDescription' });
      if (process.env.NODE_ENV === 'production') {
        setNotification({
          show: true,
          message: "Security token missing. Please refresh the page and try again.",
          title: "Error",
        });
        return;
      } else {
        console.warn('CSRF token missing in development mode - proceeding with submission');
      }
    }

    // Input validation
    if (!validateInput(description, 'description')) {
      logSecurityEvent('INVALID_INPUT_ATTEMPT', { field: 'description', value: description.substring(0, 50) });
      setNotification({
        show: true,
        message: "Invalid description format",
        title: "Error",
      });
      return;
    }

    const authToken = getAuthToken();
    if (!studioUser?.studio_id || !authToken) return;
    
    if (productDescriptionsLimitReached) {
      setNotification({
        show: true,
        message: `You can only create up to ${PRODUCT_DESCRIPTIONS_LIMIT} one-time payment descriptions per studio.`,
        title: "Limit Reached",
      });
      return;
    }
    
    setLastSubmissionTime(now);
    
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/supabase/product-descriptions`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
            "X-CSRF-Token": csrfToken,
          },
          body: JSON.stringify({
            description: sanitizeInput(description, 'description'),
            studioId: studioUser.studio_id,
          }),
        }
      );
      if (response.ok) {
        const newDescription = await response.json();
        setProductDescriptions([...productDescriptions, newDescription]);
        setNotification({
          show: true,
          message: "One-time payment description added successfully",
          title: "Success",
        });
      } else {
        throw new Error("Failed to add one-time payment description");
      }
    } catch (error) {
      logSecurityEvent('API_REQUEST_FAILED', { 
        action: 'addDescription', 
        error: error instanceof Error ? error.message : 'Unknown error' 
      });
      setNotification({
        show: true,
        message:
          error instanceof Error
            ? error.message
            : "Failed to add one-time payment description",
        title: "Error",
      });
    }
  };

  // Delete handlers
  const handleDeleteBelt = async (id: string) => {
    setItemToDelete(id);
    setDeleteType("belt");
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (deleteType === "belt") {
      await confirmDeleteBelt();
    } else if (deleteType === "membership") {
      await confirmDeleteMembership();
    } else if (deleteType === "role") {
      await confirmDeleteRole();
    } else if (deleteType === "description") {
      await confirmDeleteDescription();
    } else if (deleteType === "curriculum") {
      await confirmDeleteCurriculum();
    }
  };

  const confirmDeleteBelt = async () => {
    // Rate limiting check
    const now = Date.now();
    if (now - lastSubmissionTime < SUBMISSION_COOLDOWN) {
      setNotification({
        show: true,
        message: "Please wait before submitting again",
        title: "Error",
      });
      return;
    }

    // CSRF token validation
    if (!csrfToken) {
      logSecurityEvent('CSRF_TOKEN_MISSING', { action: 'deleteBelt' });
      if (process.env.NODE_ENV === 'production') {
        setNotification({
          show: true,
          message: "Security token missing. Please refresh the page and try again.",
          title: "Error",
        });
        return;
      } else {
        console.warn('CSRF token missing in development mode - proceeding with submission');
      }
    }

    const authToken = getAuthToken();
    if (!itemToDelete || !studioUser?.studio_id || !authToken) return;
    
    setLastSubmissionTime(now);
    
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/supabase/belts/${studioUser.studio_id}/${itemToDelete}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${authToken}`,
            "X-CSRF-Token": csrfToken,
          },
        }
      );
      if (response.ok) {
        setBelts(belts.filter((b) => b.id !== itemToDelete));
        setNotification({
          show: true,
          message: "Belt deleted successfully",
          title: "Success",
        });
      } else {
        throw new Error("Failed to delete belt");
      }
    } catch (error) {
      logSecurityEvent('API_REQUEST_FAILED', { 
        action: 'deleteBelt', 
        error: error instanceof Error ? error.message : 'Unknown error' 
      });
      setNotification({
        show: true,
        message:
          error instanceof Error ? error.message : "Failed to delete belt",
        title: "Error",
      });
    }
    setShowDeleteConfirm(false);
    setItemToDelete(null);
    setDeleteType(null);
  };

  // Curriculum handlers
  const toggleBeltExpansion = async (beltId: string) => {
    const newExpanded = new Set(expandedBelts);
    if (newExpanded.has(beltId)) {
      newExpanded.delete(beltId);
    } else {
      newExpanded.add(beltId);
      // Fetch curriculum when expanding
      if (!curriculum[beltId]) {
        await fetchCurriculum(beltId);
      }
    }
    setExpandedBelts(newExpanded);
  };

  const fetchCurriculum = async (beltId: string) => {
    const authToken = getAuthToken();
    if (!studioUser?.studio_id || !authToken) return;

    setCurriculumLoading(prev => ({ ...prev, [beltId]: true }));

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/supabase/curriculum/${studioUser.studio_id}/${beltId}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setCurriculum(prev => ({ ...prev, [beltId]: data }));
      }
    } catch (error) {
      console.error('Failed to fetch curriculum:', error);
    } finally {
      setCurriculumLoading(prev => ({ ...prev, [beltId]: false }));
    }
  };

  const beginCurriculumEdit = (item: any) => {
    setCurriculumEditId(item.id);
    setCurriculumEditTitle(item.title);
    setCurriculumEditDescription(item.description || "");
    setCurriculumEditAttachmentUrl(item.attachment_url || null);
    setCurriculumEditAttachmentFilename(item.attachment_filename || null);
    setCurriculumEditNewFile(null);
    setCurriculumEditClearAttachment(false);
  };

  const cancelCurriculumEdit = () => {
    setCurriculumEditId(null);
    setCurriculumEditNewFile(null);
    setCurriculumEditClearAttachment(false);
    setCurriculumEditAttachmentUrl(null);
    setCurriculumEditAttachmentFilename(null);
  };

  const handleAddCurriculum = (beltId: string) => {
    setAddingCurriculumBeltId(beltId);
    setNewCurriculumTitle("");
    setNewCurriculumDescription("");
    setNewCurriculumFile(null);
  };

  const handleCancelAddCurriculum = () => {
    setAddingCurriculumBeltId(null);
    setNewCurriculumTitle("");
    setNewCurriculumDescription("");
    setNewCurriculumFile(null);
  };

  const handleSaveNewCurriculum = async (beltId: string) => {
    if (!newCurriculumTitle.trim()) {
      setNotification({
        show: true,
        message: "Please enter a title",
        title: "Error",
      });
      return;
    }

    // Rate limiting check
    const now = Date.now();
    if (now - lastSubmissionTime < SUBMISSION_COOLDOWN) {
      setNotification({
        show: true,
        message: "Please wait before submitting again",
        title: "Error",
      });
      return;
    }

    const authToken = getAuthToken();
    if (!studioUser?.studio_id || !authToken) return;

    if (newCurriculumFile) {
      if (!isCurriculumPdfFile(newCurriculumFile)) {
        setNotification({
          show: true,
          message: "Please choose a PDF file (.pdf)",
          title: "Error",
        });
        return;
      }
      if (newCurriculumFile.size > CURRICULUM_PDF_MAX_BYTES) {
        setNotification({
          show: true,
          message: "PDF must be 15MB or smaller before upload",
          title: "Error",
        });
        return;
      }
    }

    setLastSubmissionTime(now);

    try {
      let attachment_url: string | null = null;
      let attachment_filename: string | null = null;
      if (newCurriculumFile) {
        const { url } = await uploadCurriculumAttachmentFile(
          newCurriculumFile,
          authToken,
          studioUser.studio_id
        );
        attachment_url = url;
        attachment_filename = newCurriculumFile.name;
      }

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/supabase/curriculum`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
            "X-CSRF-Token": csrfToken,
          },
          body: JSON.stringify({
            title: sanitizeInput(newCurriculumTitle, 'curriculumTitle'),
            description: newCurriculumDescription?.trim() ? sanitizeInput(newCurriculumDescription, 'curriculumDescription') : null,
            studioId: studioUser.studio_id,
            beltId: beltId,
            attachment_url,
            attachment_filename,
          }),
        }
      );

      if (response.ok) {
        const newItem = await response.json();
        setCurriculum(prev => ({
          ...prev,
          [beltId]: [...(prev[beltId] || []), newItem]
        }));
        setNotification({
          show: true,
          message: "Curriculum item added successfully",
          title: "Success",
        });
        handleCancelAddCurriculum();
      } else {
        throw new Error("Failed to add curriculum item");
      }
    } catch (error) {
      setNotification({
        show: true,
        message: error instanceof Error ? error.message : "Failed to add curriculum item",
        title: "Error",
      });
    }
  };

  const handleSaveCurriculumEdit = async () => {
    const id = curriculumEditId;
    if (!id) return;

    const title = curriculumEditTitle;
    const description = curriculumEditDescription;
    // Rate limiting check
    const now = Date.now();
    if (now - lastSubmissionTime < SUBMISSION_COOLDOWN) {
      setNotification({
        show: true,
        message: "Please wait before submitting again",
        title: "Error",
      });
      return;
    }

    const authToken = getAuthToken();
    if (!studioUser?.studio_id || !authToken) return;

    if (curriculumEditNewFile) {
      if (!isCurriculumPdfFile(curriculumEditNewFile)) {
        setNotification({
          show: true,
          message: "Please choose a PDF file (.pdf)",
          title: "Error",
        });
        return;
      }
      if (curriculumEditNewFile.size > CURRICULUM_PDF_MAX_BYTES) {
        setNotification({
          show: true,
          message: "PDF must be 15MB or smaller before upload",
          title: "Error",
        });
        return;
      }
    }

    setLastSubmissionTime(now);

    try {
      let attachment_url: string | null = curriculumEditAttachmentUrl;
      let attachment_filename: string | null = curriculumEditAttachmentFilename;
      if (curriculumEditClearAttachment) {
        attachment_url = null;
        attachment_filename = null;
      } else if (curriculumEditNewFile) {
        const { url } = await uploadCurriculumAttachmentFile(
          curriculumEditNewFile,
          authToken,
          studioUser.studio_id
        );
        attachment_url = url;
        attachment_filename = curriculumEditNewFile.name;
      }

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/supabase/curriculum/${studioUser.studio_id}/${id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
            "X-CSRF-Token": csrfToken,
          },
          body: JSON.stringify({
            title: sanitizeInput(title, 'curriculumTitle'),
            description: description?.trim() ? sanitizeInput(description, 'curriculumDescription') : null,
            attachment_url,
            attachment_filename,
          }),
        }
      );

      if (response.ok) {
        const updatedItem = await response.json();
        setCurriculum(prev => {
          const newCurriculum = { ...prev };
          Object.keys(newCurriculum).forEach(beltId => {
            newCurriculum[beltId] = newCurriculum[beltId].map(item =>
              item.id === id ? updatedItem : item
            );
          });
          return newCurriculum;
        });
        cancelCurriculumEdit();
        setNotification({
          show: true,
          message: "Curriculum item updated successfully",
          title: "Success",
        });
      } else {
        throw new Error("Failed to update curriculum item");
      }
    } catch (error) {
      setNotification({
        show: true,
        message: error instanceof Error ? error.message : "Failed to update curriculum item",
        title: "Error",
      });
    }
  };

  const handleDeleteCurriculum = async (id: string) => {
    setItemToDelete(id);
    setDeleteType("curriculum");
    setShowDeleteConfirm(true);
  };

  const confirmDeleteCurriculum = async () => {
    // Rate limiting check
    const now = Date.now();
    if (now - lastSubmissionTime < SUBMISSION_COOLDOWN) {
      setNotification({
        show: true,
        message: "Please wait before submitting again",
        title: "Error",
      });
      return;
    }

    const authToken = getAuthToken();
    if (!itemToDelete || !studioUser?.studio_id || !authToken) return;

    setLastSubmissionTime(now);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/supabase/curriculum/${studioUser.studio_id}/${itemToDelete}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${authToken}`,
            "X-CSRF-Token": csrfToken,
          },
        }
      );

      if (response.ok) {
        setCurriculum(prev => {
          const newCurriculum = { ...prev };
          Object.keys(newCurriculum).forEach(beltId => {
            newCurriculum[beltId] = newCurriculum[beltId].filter(item => item.id !== itemToDelete);
          });
          return newCurriculum;
        });
        if (curriculumEditId === itemToDelete) {
          cancelCurriculumEdit();
        }
        setNotification({
          show: true,
          message: "Curriculum item deleted successfully",
          title: "Success",
        });
      } else {
        throw new Error("Failed to delete curriculum item");
      }
    } catch (error) {
      setNotification({
        show: true,
        message: error instanceof Error ? error.message : "Failed to delete curriculum item",
        title: "Error",
      });
    }
    setShowDeleteConfirm(false);
    setItemToDelete(null);
    setDeleteType(null);
  };

  const handleCurriculumDragEnd = async (event: DragEndEvent, beltId: string) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const beltCurriculum = curriculum[beltId] || [];
    const oldIndex = beltCurriculum.findIndex(item => item.id === active.id);
    const newIndex = beltCurriculum.findIndex(item => item.id === over.id);

    if (oldIndex !== -1 && newIndex !== -1) {
      // Update local state immediately
      const newCurriculum = arrayMove(beltCurriculum, oldIndex, newIndex).map((item, index) => ({
        ...item,
        order: index + 1
      }));
      
      setCurriculum(prev => ({ ...prev, [beltId]: newCurriculum }));

      // Update all curriculum items with their new order values
      const authToken = getAuthToken();
      if (studioUser?.studio_id && authToken) {
        try {
          const updatePromises = newCurriculum.map(item =>
            fetch(
              `${process.env.REACT_APP_BACKEND_URL}/api/supabase/curriculum/${studioUser.studio_id}/${item.id}`,
              {
                method: "PUT",
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${authToken}`,
                  "X-CSRF-Token": csrfToken,
                },
                body: JSON.stringify({ order: item.order }),
              }
            )
          );
          await Promise.all(updatePromises);
        } catch (error) {
          console.error('Failed to update curriculum order:', error);
          setNotification({
            show: true,
            message: "Failed to save curriculum order",
            title: "Error",
          });
          // Revert on error
          fetchCurriculum(beltId);
        }
      }
    }
  };

  const handleDeleteMembership = async (id: string) => {
    setItemToDelete(id);
    setDeleteType("membership");
    setShowDeleteConfirm(true);
  };

  const confirmDeleteMembership = async () => {
    // Rate limiting check
    const now = Date.now();
    if (now - lastSubmissionTime < SUBMISSION_COOLDOWN) {
      setNotification({
        show: true,
        message: "Please wait before submitting again",
        title: "Error",
      });
      return;
    }

    // CSRF token validation
    if (!csrfToken) {
      logSecurityEvent('CSRF_TOKEN_MISSING', { action: 'deleteMembership' });
      if (process.env.NODE_ENV === 'production') {
        setNotification({
          show: true,
          message: "Security token missing. Please refresh the page and try again.",
          title: "Error",
        });
        return;
      } else {
        console.warn('CSRF token missing in development mode - proceeding with submission');
      }
    }

    const authToken = getAuthToken();
    if (!itemToDelete || !studioUser?.studio_id || !authToken) return;
    
    setLastSubmissionTime(now);
    
    try {
      const headers = {
        Authorization: `Bearer ${authToken}`,
        "X-CSRF-Token": csrfToken,
      };

      if (canManageStripeMemberships) {
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/stripe/memberships/${itemToDelete}?studio_id=${studioUser.studio_id}`,
          { method: "DELETE", headers }
        );
        if (response.ok) {
          const result = await response.json();
          setMemberships(memberships.filter((m) => m.id !== itemToDelete));
          setNotification({
            show: true,
            message:
              result.details ||
              "Membership and Stripe resources deleted successfully",
            title: "Success",
          });
        } else {
          const errorData = await response.json();
          throw new Error(
            errorData.details || errorData.error || "Failed to delete membership"
          );
        }
      } else {
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/supabase/memberships/${studioUser.studio_id}/${itemToDelete}`,
          { method: "DELETE", headers }
        );
        if (response.ok) {
          setMemberships(memberships.filter((m) => m.id !== itemToDelete));
          setNotification({
            show: true,
            message: "Membership deleted successfully",
            title: "Success",
          });
        } else {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error || "Failed to delete membership");
        }
      }
    } catch (error) {
      logSecurityEvent('API_REQUEST_FAILED', { 
        action: 'deleteMembership', 
        error: error instanceof Error ? error.message : 'Unknown error' 
      });
      setNotification({
        show: true,
        message:
          error instanceof Error
            ? error.message
            : "Failed to delete membership and Stripe resources",
        title: "Error",
      });
    }
    setShowDeleteConfirm(false);
    setItemToDelete(null);
    setDeleteType(null);
  };

  const handleDeleteRole = async (id: string) => {
    setItemToDelete(id);
    setDeleteType("role");
    setShowDeleteConfirm(true);
  };

  const handleDeleteDescription = async (id: string) => {
    setItemToDelete(id);
    setDeleteType("description");
    setShowDeleteConfirm(true);
  };

  const confirmDeleteRole = async () => {
    // Rate limiting check
    const now = Date.now();
    if (now - lastSubmissionTime < SUBMISSION_COOLDOWN) {
      setNotification({
        show: true,
        message: "Please wait before submitting again",
        title: "Error",
      });
      return;
    }

    // CSRF token validation
    if (!csrfToken) {
      logSecurityEvent('CSRF_TOKEN_MISSING', { action: 'deleteRole' });
      if (process.env.NODE_ENV === 'production') {
        setNotification({
          show: true,
          message: "Security token missing. Please refresh the page and try again.",
          title: "Error",
        });
        return;
      } else {
        console.warn('CSRF token missing in development mode - proceeding with submission');
      }
    }

    const authToken = getAuthToken();
    if (!itemToDelete || !studioUser?.studio_id || !authToken) return;
    
    setLastSubmissionTime(now);
    
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/supabase/staff-roles/${studioUser.studio_id}/${itemToDelete}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${authToken}`,
            "X-CSRF-Token": csrfToken,
          },
        }
      );
      if (response.ok) {
        setStaffRoles(staffRoles.filter((r) => r.id !== itemToDelete));
        setNotification({
          show: true,
          message: "Staff role deleted successfully",
          title: "Success",
        });
      } else {
        throw new Error("Failed to delete staff role");
      }
    } catch (error) {
      logSecurityEvent('API_REQUEST_FAILED', { 
        action: 'deleteRole', 
        error: error instanceof Error ? error.message : 'Unknown error' 
      });
      setNotification({
        show: true,
        message:
          error instanceof Error
            ? error.message
            : "Failed to delete staff role",
        title: "Error",
      });
    }
    setShowDeleteConfirm(false);
    setItemToDelete(null);
    setDeleteType(null);
  };

  const confirmDeleteDescription = async () => {
    // Rate limiting check
    const now = Date.now();
    if (now - lastSubmissionTime < SUBMISSION_COOLDOWN) {
      setNotification({
        show: true,
        message: "Please wait before submitting again",
        title: "Error",
      });
      return;
    }

    // CSRF token validation
    if (!csrfToken) {
      logSecurityEvent('CSRF_TOKEN_MISSING', { action: 'deleteDescription' });
      if (process.env.NODE_ENV === 'production') {
        setNotification({
          show: true,
          message: "Security token missing. Please refresh the page and try again.",
          title: "Error",
        });
        return;
      } else {
        console.warn('CSRF token missing in development mode - proceeding with submission');
      }
    }

    const authToken = getAuthToken();
    if (!itemToDelete || !studioUser?.studio_id || !authToken) return;
    
    setLastSubmissionTime(now);
    
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/supabase/product-descriptions/${studioUser.studio_id}/${itemToDelete}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${authToken}`,
            "X-CSRF-Token": csrfToken,
          },
        }
      );
      if (response.ok) {
        setProductDescriptions(
          productDescriptions.filter((d) => d.id !== itemToDelete)
        );
        setNotification({
          show: true,
          message: "One-time payment description deleted successfully",
          title: "Success",
        });
      } else {
        throw new Error("Failed to delete one-time payment description");
      }
    } catch (error) {
      logSecurityEvent('API_REQUEST_FAILED', { 
        action: 'deleteDescription', 
        error: error instanceof Error ? error.message : 'Unknown error' 
      });
      setNotification({
        show: true,
        message:
          error instanceof Error
            ? error.message
            : "Failed to delete one-time payment description",
        title: "Error",
      });
    }
    setShowDeleteConfirm(false);
    setItemToDelete(null);
    setDeleteType(null);
  };

  const handleSaveLeadVisibility = async (newValue: boolean) => {
    // Rate limiting check
    const now = Date.now();
    if (now - lastSubmissionTime < SUBMISSION_COOLDOWN) {
      setNotification({
        show: true,
        message: "Please wait before submitting again",
        title: "Error",
      });
      return;
    }

    // CSRF token validation
    if (!csrfToken) {
      logSecurityEvent('CSRF_TOKEN_MISSING', { action: 'updateLeadVisibility' });
      if (process.env.NODE_ENV === 'production') {
        setNotification({
          show: true,
          message: "Security token missing. Please refresh the page and try again.",
          title: "Error",
        });
        return;
      } else {
        console.warn('CSRF token missing in development mode - proceeding with submission');
      }
    }

    const authToken = getAuthToken();
    if (!studioUser?.studio_id || !authToken) return;
    
    setLastSubmissionTime(now);
    
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/supabase/lead-visibility/${studioUser.studio_id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
            "X-CSRF-Token": csrfToken,
          },
          body: JSON.stringify({ isEnabled: newValue }),
        }
      );
      if (response.ok) {
        setLeadVisibility(newValue);
        setNotification({
          show: true,
          message: "Lead visibility updated successfully",
          title: "Success",
        });
      } else {
        throw new Error("Failed to update lead visibility");
      }
    } catch (error) {
      logSecurityEvent('API_REQUEST_FAILED', { 
        action: 'updateLeadVisibility', 
        error: error instanceof Error ? error.message : 'Unknown error' 
      });
      setNotification({
        show: true,
        message:
          error instanceof Error
            ? error.message
            : "Failed to update lead visibility",
        title: "Error",
      });
    }
  };

  const handleSaveAutoAppointmentSms = async (newValue: boolean) => {
    // Rate limiting check
    const now = Date.now();
    if (now - lastSubmissionTime < SUBMISSION_COOLDOWN) {
      setNotification({
        show: true,
        message: "Please wait before submitting again",
        title: "Error",
      });
      return;
    }

    // CSRF token validation
    if (!csrfToken) {
      logSecurityEvent('CSRF_TOKEN_MISSING', { action: 'updateAutoAppointmentSms' });
      if (process.env.NODE_ENV === 'production') {
        setNotification({
          show: true,
          message: "Security token missing. Please refresh the page and try again.",
          title: "Error",
        });
        return;
      } else {
        console.warn('CSRF token missing in development mode - proceeding with submission');
      }
    }

    const authToken = getAuthToken();
    if (!studioUser?.studio_id || !authToken) return;
    
    setLastSubmissionTime(now);
    
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/supabase/studios/${studioUser.studio_id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
            "X-CSRF-Token": csrfToken,
          },
          body: JSON.stringify({ auto_appointment_sms_enabled: newValue }),
        }
      );
      if (response.ok) {
        setAutoAppointmentSms(newValue);
        setNotification({
          show: true,
          message: "Auto appointment SMS setting updated successfully",
          title: "Success",
        });
      } else {
        throw new Error("Failed to update auto appointment SMS setting");
      }
    } catch (error) {
      logSecurityEvent('API_REQUEST_FAILED', { 
        action: 'updateAutoAppointmentSms', 
        error: error instanceof Error ? error.message : 'Unknown error' 
      });
      setNotification({
        show: true,
        message:
          error instanceof Error
            ? error.message
            : "Failed to update auto appointment SMS setting",
        title: "Error",
      });
    }
  };

  const handleSaveAutoSmsMessage = async () => {
    // Rate limiting check
    const now = Date.now();
    if (now - lastSubmissionTime < SUBMISSION_COOLDOWN) {
      setNotification({
        show: true,
        message: "Please wait before submitting again",
        title: "Error",
      });
      return;
    }

    // CSRF token validation
    if (!csrfToken) {
      logSecurityEvent('CSRF_TOKEN_MISSING', { action: 'updateAutoSmsMessage' });
      if (process.env.NODE_ENV === 'production') {
        setNotification({
          show: true,
          message: "Security token missing. Please refresh the page and try again.",
          title: "Error",
        });
        return;
      } else {
        console.warn('CSRF token missing in development mode - proceeding with submission');
      }
    }

    const authToken = getAuthToken();
    if (!studioUser?.studio_id || !authToken) return;
    
    setLastSubmissionTime(now);
    setAutoSmsMessageLoading(true);
    
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/supabase/studios/${studioUser.studio_id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
            "X-CSRF-Token": csrfToken,
          },
          body: JSON.stringify({ auto_appointment_sms_message: autoSmsMessage }),
        }
      );
      if (response.ok) {
        setNotification({
          show: true,
          message: "Auto appointment SMS message updated successfully",
          title: "Success",
        });
      } else {
        throw new Error("Failed to update auto appointment SMS message");
      }
    } catch (error) {
      logSecurityEvent('API_REQUEST_FAILED', { 
        action: 'updateAutoSmsMessage', 
        error: error instanceof Error ? error.message : 'Unknown error' 
      });
      setNotification({
        show: true,
        message:
          error instanceof Error
            ? error.message
            : "Failed to update auto appointment SMS message",
        title: "Error",
      });
    } finally {
      setAutoSmsMessageLoading(false);
    }
  };

  const handleSaveFollowUpSmsMessage = async (messageNumber: 2 | 3) => {
    // Rate limiting check
    const now = Date.now();
    if (now - lastSubmissionTime < SUBMISSION_COOLDOWN) {
      setNotification({
        show: true,
        message: "Please wait before submitting again",
        title: "Error",
      });
      return;
    }

    // CSRF token validation
    if (!csrfToken) {
      logSecurityEvent('CSRF_TOKEN_MISSING', { action: `updateFollowUpSmsMessage${messageNumber}` });
      if (process.env.NODE_ENV === 'production') {
        setNotification({
          show: true,
          message: "Security token missing. Please refresh the page and try again.",
          title: "Error",
        });
        return;
      } else {
        console.warn('CSRF token missing in development mode - proceeding with submission');
      }
    }

    const authToken = getAuthToken();
    if (!studioUser?.studio_id || !authToken) return;
    
    setLastSubmissionTime(now);
    if (messageNumber === 2) {
      setFollowUpSmsMessage2Loading(true);
    } else {
      setFollowUpSmsMessage3Loading(true);
    }
    
    const fieldName = messageNumber === 2 ? 'follow_up_sms_message_2' : 'follow_up_sms_message_3';
    const messageValue = messageNumber === 2 ? followUpSmsMessage2 : followUpSmsMessage3;
    
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/supabase/studios/${studioUser.studio_id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
            "X-CSRF-Token": csrfToken,
          },
          body: JSON.stringify({ [fieldName]: messageValue || null }),
        }
      );
      if (response.ok) {
        setNotification({
          show: true,
          message: `Follow-up SMS message #${messageNumber} updated successfully`,
          title: "Success",
        });
      } else {
        throw new Error(`Failed to update follow-up SMS message #${messageNumber}`);
      }
    } catch (error) {
      logSecurityEvent('API_REQUEST_FAILED', { 
        action: `updateFollowUpSmsMessage${messageNumber}`, 
        error: error instanceof Error ? error.message : 'Unknown error' 
      });
      setNotification({
        show: true,
        message:
          error instanceof Error
            ? error.message
            : `Failed to update follow-up SMS message #${messageNumber}`,
        title: "Error",
      });
    } finally {
      if (messageNumber === 2) {
        setFollowUpSmsMessage2Loading(false);
      } else {
        setFollowUpSmsMessage3Loading(false);
      }
    }
  };

  const handleSaveMissedAppointmentSmsMessage = async () => {
    // Rate limiting check
    const now = Date.now();
    if (now - lastSubmissionTime < SUBMISSION_COOLDOWN) {
      setNotification({
        show: true,
        message: "Please wait before submitting again",
        title: "Error",
      });
      return;
    }

    // CSRF token validation
    if (!csrfToken) {
      logSecurityEvent('CSRF_TOKEN_MISSING', { action: 'updateMissedAppointmentSmsMessage' });
      if (process.env.NODE_ENV === 'production') {
        setNotification({
          show: true,
          message: "Security token missing. Please refresh the page and try again.",
          title: "Error",
        });
        return;
      } else {
        console.warn('CSRF token missing in development mode - proceeding with submission');
      }
    }

    const authToken = getAuthToken();
    if (!studioUser?.studio_id || !authToken) return;
    
    setLastSubmissionTime(now);
    setMissedAppointmentSmsMessageLoading(true);
    
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/supabase/studios/${studioUser.studio_id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
            "X-CSRF-Token": csrfToken,
          },
          body: JSON.stringify({ missed_appointment_sms_message: missedAppointmentSmsMessage || null }),
        }
      );
      if (response.ok) {
        setNotification({
          show: true,
          message: "Missed appointment SMS message updated successfully",
          title: "Success",
        });
      } else {
        throw new Error("Failed to update missed appointment SMS message");
      }
    } catch (error) {
      logSecurityEvent('API_REQUEST_FAILED', { 
        action: 'updateMissedAppointmentSmsMessage', 
        error: error instanceof Error ? error.message : 'Unknown error' 
      });
      setNotification({
        show: true,
        message:
          error instanceof Error
            ? error.message
            : "Failed to update missed appointment SMS message",
        title: "Error",
      });
    } finally {
      setMissedAppointmentSmsMessageLoading(false);
    }
  };

  const handleSaveBeltEdit = async (id: string, newName: string) => {
    // Rate limiting check
    const now = Date.now();
    if (now - lastSubmissionTime < SUBMISSION_COOLDOWN) {
      setNotification({
        show: true,
        message: "Please wait before submitting again",
        title: "Error",
      });
      return;
    }

    // CSRF token validation
    if (!csrfToken) {
      logSecurityEvent('CSRF_TOKEN_MISSING', { action: 'editBelt' });
      if (process.env.NODE_ENV === 'production') {
        setNotification({
          show: true,
          message: "Security token missing. Please refresh the page and try again.",
          title: "Error",
        });
        return;
      } else {
        console.warn('CSRF token missing in development mode - proceeding with submission');
      }
    }

    // Input validation
    if (!validateInput(newName, 'beltName')) {
      logSecurityEvent('INVALID_INPUT_ATTEMPT', { field: 'beltName', value: newName.substring(0, 50) });
      setNotification({
        show: true,
        message: "Invalid belt name format",
        title: "Error",
      });
      return;
    }

    const authToken = getAuthToken();
    if (!studioUser?.studio_id || !authToken) return;
    
    setLastSubmissionTime(now);
    
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/supabase/belts/${studioUser.studio_id}/${id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
            "X-CSRF-Token": csrfToken,
          },
          body: JSON.stringify({ name: sanitizeInput(newName, 'beltName') }),
        }
      );
      if (response.ok) {
        setBelts(belts.map((b) => (b.id === id ? { ...b, name: sanitizeInput(newName, 'beltName') } : b)));
        setBeltEditId(null);
        setNotification({
          show: true,
          message: "Belt updated successfully",
          title: "Success",
        });
      } else {
        throw new Error("Failed to update belt");
      }
    } catch (error) {
      logSecurityEvent('API_REQUEST_FAILED', { 
        action: 'editBelt', 
        error: error instanceof Error ? error.message : 'Unknown error' 
      });
      setNotification({
        show: true,
        message:
          error instanceof Error ? error.message : "Failed to update belt",
        title: "Error",
      });
      setBeltEditId(null);
    }
  };

  const handleSaveMembershipEdit = async (
    id: string,
    newName: string,
    newPrice: number
  ) => {
    // Rate limiting check
    const now = Date.now();
    if (now - lastSubmissionTime < SUBMISSION_COOLDOWN) {
      setNotification({
        show: true,
        message: "Please wait before submitting again",
        title: "Error",
      });
      return;
    }

    // CSRF token validation
    if (!csrfToken) {
      logSecurityEvent('CSRF_TOKEN_MISSING', { action: 'editMembership' });
      if (process.env.NODE_ENV === 'production') {
        setNotification({
          show: true,
          message: "Security token missing. Please refresh the page and try again.",
          title: "Error",
        });
        return;
      } else {
        console.warn('CSRF token missing in development mode - proceeding with submission');
      }
    }

    // Input validation
    if (!validateInput(newName, 'membershipName')) {
      logSecurityEvent('INVALID_INPUT_ATTEMPT', { field: 'membershipName', value: newName.substring(0, 50) });
      setNotification({
        show: true,
        message: "Invalid membership name format",
        title: "Error",
      });
      return;
    }

    if (!validateInput(newPrice.toString(), 'membershipPrice')) {
      logSecurityEvent('INVALID_INPUT_ATTEMPT', { field: 'membershipPrice', value: newPrice.toString() });
      setNotification({
        show: true,
        message: "Invalid price format",
        title: "Error",
      });
      return;
    }

    const authToken = getAuthToken();
    if (!studioUser?.studio_id || !authToken) return;
    
    setLastSubmissionTime(now);
    
    try {
      const sanitizedName = sanitizeInput(newName, 'membershipName');
      const headers = {
        "Content-Type": "application/json",
        Authorization: `Bearer ${authToken}`,
        "X-CSRF-Token": csrfToken,
      };

      if (canManageStripeMemberships) {
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/stripe/memberships/${id}`,
          {
            method: "PUT",
            headers,
            body: JSON.stringify({
              name: sanitizedName,
              price: newPrice,
              studio_id: studioUser.studio_id,
            }),
          }
        );
        if (response.ok) {
          const updatedMembership = await response.json();
          setMemberships(
            memberships.map((m) =>
              m.id === id
                ? { ...m, name: sanitizedName, price: newPrice, ...updatedMembership }
                : m
            )
          );
          setMembershipEditId(null);
          setMembershipEditValue("");
          setMembershipEditPrice("");
          setNotification({
            show: true,
            message:
              "Membership and Stripe pricing updated successfully. All active subscriptions have been updated to use the new price and will be charged the new amount on their next billing cycle.",
            title: "Success",
          });
        } else {
          const errorData = await response.json();
          throw new Error(errorData.error || "Failed to update membership");
        }
      } else {
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/supabase/memberships/${studioUser.studio_id}/${id}`,
          {
            method: "PUT",
            headers,
            body: JSON.stringify({
              name: sanitizedName,
              price: newPrice,
            }),
          }
        );
        if (response.ok) {
          const updatedMembership = await response.json();
          setMemberships(
            memberships.map((m) =>
              m.id === id
                ? { ...m, name: sanitizedName, price: newPrice, ...updatedMembership }
                : m
            )
          );
          setMembershipEditId(null);
          setMembershipEditValue("");
          setMembershipEditPrice("");
          setNotification({
            show: true,
            message: "Membership updated successfully",
            title: "Success",
          });
        } else {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error || "Failed to update membership");
        }
      }
    } catch (error) {
      logSecurityEvent('API_REQUEST_FAILED', { 
        action: 'editMembership', 
        error: error instanceof Error ? error.message : 'Unknown error' 
      });
      setNotification({
        show: true,
        message:
          error instanceof Error
            ? error.message
            : "Failed to update membership and Stripe pricing",
        title: "Error",
      });
      setMembershipEditId(null);
      setMembershipEditValue("");
      setMembershipEditPrice("");
    }
  };

  const handleSaveRoleEdit = async (id: string, newName: string) => {
    // Rate limiting check
    const now = Date.now();
    if (now - lastSubmissionTime < SUBMISSION_COOLDOWN) {
      setNotification({
        show: true,
        message: "Please wait before submitting again",
        title: "Error",
      });
      return;
    }

    // CSRF token validation
    if (!csrfToken) {
      logSecurityEvent('CSRF_TOKEN_MISSING', { action: 'editRole' });
      if (process.env.NODE_ENV === 'production') {
        setNotification({
          show: true,
          message: "Security token missing. Please refresh the page and try again.",
          title: "Error",
        });
        return;
      } else {
        console.warn('CSRF token missing in development mode - proceeding with submission');
      }
    }

    // Input validation
    if (!validateInput(newName, 'roleName')) {
      logSecurityEvent('INVALID_INPUT_ATTEMPT', { field: 'roleName', value: newName.substring(0, 50) });
      setNotification({
        show: true,
        message: "Invalid role name format",
        title: "Error",
      });
      return;
    }

    const authToken = getAuthToken();
    if (!studioUser?.studio_id || !authToken) return;
    
    setLastSubmissionTime(now);
    
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/supabase/staff-roles/${studioUser.studio_id}/${id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
            "X-CSRF-Token": csrfToken,
          },
          body: JSON.stringify({ name: sanitizeInput(newName, 'roleName') }),
        }
      );
      if (response.ok) {
        setStaffRoles(
          staffRoles.map((r) => (r.id === id ? { ...r, name: sanitizeInput(newName, 'roleName') } : r))
        );
        setRoleEditId(null);
        setNotification({
          show: true,
          message: "Staff role updated successfully",
          title: "Success",
        });
      } else {
        throw new Error("Failed to update staff role");
      }
    } catch (error) {
      logSecurityEvent('API_REQUEST_FAILED', { 
        action: 'editRole', 
        error: error instanceof Error ? error.message : 'Unknown error' 
      });
      setNotification({
        show: true,
        message:
          error instanceof Error
            ? error.message
            : "Failed to update staff role",
        title: "Error",
      });
      setRoleEditId(null);
    }
  };

  const handleSaveDescriptionEdit = async (
    id: string,
    newDescription: string
  ) => {
    // Rate limiting check
    const now = Date.now();
    if (now - lastSubmissionTime < SUBMISSION_COOLDOWN) {
      setNotification({
        show: true,
        message: "Please wait before submitting again",
        title: "Error",
      });
      return;
    }

    // CSRF token validation
    if (!csrfToken) {
      logSecurityEvent('CSRF_TOKEN_MISSING', { action: 'editDescription' });
      if (process.env.NODE_ENV === 'production') {
        setNotification({
          show: true,
          message: "Security token missing. Please refresh the page and try again.",
          title: "Error",
        });
        return;
      } else {
        console.warn('CSRF token missing in development mode - proceeding with submission');
      }
    }

    // Input validation
    if (!validateInput(newDescription, 'description')) {
      logSecurityEvent('INVALID_INPUT_ATTEMPT', { field: 'description', value: newDescription.substring(0, 50) });
      setNotification({
        show: true,
        message: "Invalid description format",
        title: "Error",
      });
      return;
    }

    const authToken = getAuthToken();
    if (!studioUser?.studio_id || !authToken) return;
    
    setLastSubmissionTime(now);
    
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/supabase/product-descriptions/${studioUser.studio_id}/${id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
            "X-CSRF-Token": csrfToken,
          },
          body: JSON.stringify({ description: sanitizeInput(newDescription, 'description') }),
        }
      );
      if (response.ok) {
        setProductDescriptions(
          productDescriptions.map((d) =>
            d.id === id ? { ...d, description: sanitizeInput(newDescription, 'description') } : d
          )
        );
        setDescriptionEditId(null);
        setNotification({
          show: true,
          message: "One-time payment description updated successfully",
          title: "Success",
        });
      } else {
        throw new Error("Failed to update one-time payment description");
      }
    } catch (error) {
      logSecurityEvent('API_REQUEST_FAILED', { 
        action: 'editDescription', 
        error: error instanceof Error ? error.message : 'Unknown error' 
      });
      setNotification({
        show: true,
        message:
          error instanceof Error
            ? error.message
            : "Failed to update one-time payment description",
        title: "Error",
      });
      setDescriptionEditId(null);
    }
  };

  const handleRegenerateStudioCode = async () => {
    // Rate limiting check
    const now = Date.now();
    if (now - lastSubmissionTime < SUBMISSION_COOLDOWN) {
      setNotification({
        show: true,
        message: "Please wait before submitting again",
        title: "Error",
      });
      return;
    }

    // CSRF token validation
    if (!csrfToken) {
      logSecurityEvent('CSRF_TOKEN_MISSING', { action: 'regenerateStudioCode' });
      if (process.env.NODE_ENV === 'production') {
        setNotification({
          show: true,
          message: "Security token missing. Please refresh the page and try again.",
          title: "Error",
        });
        return;
      } else {
        console.warn('CSRF token missing in development mode - proceeding with submission');
      }
    }

    if (!studioUser?.studio_id) return;

    const authToken = getAuthToken();
    if (!authToken) return;

    setRotatingCode(true);
    setLastSubmissionTime(now);
    
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/supabase/studios/${studioUser.studio_id}/rotate-code`,
        {
          method: "POST",
          headers: { 
            Authorization: `Bearer ${authToken}`,
            "X-CSRF-Token": csrfToken,
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to regenerate studio code");
      }

      const result = await response.json();
      setStudioCode(result.newCode);

      // Update code info
      setStudioCodeInfo((prev) =>
        prev
          ? {
              ...prev,
              previousCode: result.previousCode,
              lastChanged: new Date().toISOString(),
              expiresAt: result.expiresAt,
              daysUntilExpiration: 90, // 3 months
              needsRotation: false,
            }
          : null
      );

      setNotification({
        show: true,
        message:
          "Studio code regenerated successfully! Please share the new code with your staff.",
        title: "Success",
      });
    } catch (error) {
      logSecurityEvent('API_REQUEST_FAILED', { 
        action: 'regenerateStudioCode', 
        error: error instanceof Error ? error.message : 'Unknown error' 
      });
      setNotification({
        show: true,
        message:
          error instanceof Error
            ? error.message
            : "Failed to regenerate studio code",
        title: "Error",
      });
    } finally {
      setRotatingCode(false);
    }
  };

  const handleSaveRiskThresholds = async () => {
    // Rate limiting check
    const now = Date.now();
    if (now - lastSubmissionTime < SUBMISSION_COOLDOWN) {
      setNotification({
        show: true,
        message: "Please wait before submitting again",
        title: "Error",
      });
      return;
    }

    // CSRF token validation
    if (!csrfToken) {
      logSecurityEvent('CSRF_TOKEN_MISSING', { action: 'saveRiskThresholds' });
      if (process.env.NODE_ENV === 'production') {
        setNotification({
          show: true,
          message: "Security token missing. Please refresh the page and try again.",
          title: "Error",
        });
        return;
      } else {
        console.warn('CSRF token missing in development mode - proceeding with submission');
      }
    }

    // Input validation for risk threshold
    if (highRiskThreshold < 0 || highRiskThreshold > 100 || isNaN(highRiskThreshold)) {
      setNotification({
        show: true,
        message: "Invalid risk threshold value",
        title: "Error",
      });
      return;
    }

    const authToken = getAuthToken();
    if (!studioUser?.studio_id || !authToken) return;

    setLastSubmissionTime(now);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/supabase/studios/${studioUser.studio_id}/risk-thresholds`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
            "X-CSRF-Token": csrfToken,
          },
          body: JSON.stringify({
            high_risk_threshold: highRiskThreshold / 100,
            sms_message: dropoutSmsMessage,
            email_message: dropoutEmailMessage,
            send_sms: canSendSms && sendDropoutSms,
            send_email: sendDropoutEmail,
          }),
        }
      );

      if (response.ok) {
        // Refetch the updated risk thresholds to ensure UI is in sync
        const fetchResponse = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/supabase/studios/${studioUser.studio_id}/risk-thresholds`,
          {
            headers: { 
              Authorization: `Bearer ${authToken}`,
              "X-CSRF-Token": csrfToken,
            },
          }
        );

        if (fetchResponse.ok) {
          const data = await fetchResponse.json();
          if (data && typeof data.threshold === "number") {
            setHighRiskThreshold(data.threshold * 100);
          }
          if (data?.sms_message !== undefined) {
            setDropoutSmsMessage(data.sms_message || "");
          }
          if (data?.email_message !== undefined) {
            setDropoutEmailMessage(data.email_message || "");
          }
          if (data?.send_sms !== undefined) {
            setSendDropoutSms(data.send_sms || false);
          }
          if (data?.send_email !== undefined) {
            setSendDropoutEmail(data.send_email || false);
          }
        }

        setNotification({
          show: true,
          message: "AI risk thresholds updated successfully",
          title: "Success",
        });
      } else {
        throw new Error("Failed to update AI risk thresholds");
      }
    } catch (error) {
      logSecurityEvent('API_REQUEST_FAILED', { 
        action: 'saveRiskThresholds', 
        error: error instanceof Error ? error.message : 'Unknown error' 
      });
      setNotification({
        show: true,
        message:
          error instanceof Error
            ? error.message
            : "Failed to update AI risk thresholds",
        title: "Error",
      });
    }
  };

  const canAddMembership =
    !planLoading && (canManageStripeMemberships ? isStripeConnected : true);

  return (
    <div className="space-y-6">
        <Notification
          show={notification.show}
          message={notification.message}
          title={notification.title}
          onClose={() =>
            setNotification((prev) => ({
              ...prev,
              show: false,
            }))
          }
        />
        {/* Studio Code */}
        <section className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 w-full">
          <div className="flex items-center justify-between mb-2">
            <Tooltip text="Your unique studio code that staff members use to log in. Share this code with your staff so they can access their accounts.">
              <h2 className="text-lg font-medium leading-6 text-gray-900 dark:text-white cursor-help">
                Studio Code
              </h2>
            </Tooltip>
          </div>
          <div className="mt-4">
            <div className="flex items-center space-x-4">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Your Studio Code{" "}
                  <span className="text-gray-500 dark:text-gray-400 font-normal italic">
                    (rotates every three months for security)
                  </span>
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={studioCode}
                    readOnly
                    className={getInputClassName(
                      "block w-full rounded-md border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 shadow-sm sm:text-sm px-3 py-2"
                    )}
                    placeholder="Loading studio code..."
                  />
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(studioCode);
                      setNotification({
                        show: true,
                        message: "Studio code copied to clipboard!",
                        title: "Success",
                      });
                    }}
                    className="absolute inset-y-0 right-0 px-3 flex items-center text-sm font-medium p-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-100 dark:hover:bg-gray-600 transition text-indigo-600 dark:text-indigo-400"
                    disabled={!studioCode}
                  >
                    <span className="pointer-events-none">Copy</span>
                  </button>
                </div>

                {/* Studio Code Info */}
                {studioCodeInfo && (
                  <div className="mt-3 space-y-2">
                    <div className="text-sm">
                      <span className="text-gray-600 dark:text-gray-400">
                        Expires:{" "}
                      </span>
                      <span
                        className={`font-medium ${
                          studioCodeInfo.needsRotation
                            ? "text-orange-600 dark:text-orange-400"
                            : "text-gray-900 dark:text-white"
                        }`}
                      >
                        {new Date(
                          studioCodeInfo.expiresAt
                        ).toLocaleDateString()}{" "}
                        ({studioCodeInfo.daysUntilExpiration} days)
                      </span>
                    </div>
                    {studioCodeInfo.needsRotation && (
                      <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-md p-3">
                        <div className="flex items-center">
                          <div className="flex-shrink-0">
                            <svg
                              className="h-5 w-5 text-orange-400"
                              viewBox="0 0 20 20"
                              fill="currentColor"
                            >
                              <path
                                fillRule="evenodd"
                                d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </div>
                          <div className="ml-3">
                            <p className="text-sm text-orange-800 dark:text-orange-200">
                              Your studio code expires in{" "}
                              {studioCodeInfo.needsRotation} days.
                              Consider regenerating it soon.
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Staff Login URL — paid plans with sub-accounts */}
                {planLoading ? (
                  <div className="mt-6 space-y-2 animate-pulse">
                    <div className="h-4 w-28 bg-gray-200 dark:bg-gray-600 rounded" />
                    <div className="h-10 w-full bg-gray-200 dark:bg-gray-600 rounded-md" />
                  </div>
                ) : showStaffLoginUrl ? (
                <div className="mt-6">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Staff Login URL
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={`${window.location.origin}/staff-login/${studioCode}`}
                      readOnly
                      className={getInputClassName(
                        "block w-full rounded-md border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 shadow-sm sm:text-sm px-3 py-2 pr-20"
                      )}
                      placeholder="Loading staff login URL..."
                    />
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(`${window.location.origin}/staff-login/${studioCode}`);
                        setNotification({
                          show: true,
                          message: "Staff login URL copied to clipboard!",
                          title: "Success",
                        });
                      }}
                      className="absolute inset-y-0 right-0 px-3 flex items-center text-sm font-medium p-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-100 dark:hover:bg-gray-600 transition text-indigo-600 dark:text-indigo-400"
                      disabled={!studioCode}
                    >
                      <span className="pointer-events-none">Copy URL</span>
                    </button>
                  </div>
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                    Share this URL with your staff members for secure access to their accounts.
                  </p>
                </div>
                ) : null}
              </div>
            </div>

            {/* Regenerate Button */}
            <div className="mt-4 flex justify-start">
              <button
                onClick={handleRegenerateStudioCode}
                disabled={rotatingCode || !studioCode}
                className="inline-flex items-center px-3 py-2 border border-orange-300 dark:border-orange-600 shadow-sm text-sm leading-4 font-medium rounded-md text-orange-700 dark:text-orange-300 bg-orange-50 dark:bg-orange-900/20 hover:bg-orange-100 dark:hover:bg-orange-900/30 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 disabled:opacity-50"
              >
                {rotatingCode ? (
                  <>
                    <svg
                      className="animate-spin -ml-1 mr-2 h-4 w-4 text-orange-700 dark:text-orange-300 pointer-events-none"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    <span className="pointer-events-none">Regenerating...</span>
                  </>
                ) : (
                  <span className="pointer-events-none">Regenerate Code</span>
                )}
              </button>
            </div>
          </div>
        </section>

        {/* Retention Risk Thresholds */}
        {planLoading ? (
          <SettingsPlanLoadingSection title="High Retention Risk Threshold" />
        ) : !hasCapability('aiDropoffRisk') ? (
        <section className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 w-full">
          <h2 className="text-lg font-medium leading-6 text-gray-900 dark:text-white mb-4">
            High Retention Risk Threshold
          </h2>
          <UpgradePrompt capability="aiDropoffRisk" hideTitle />
        </section>
        ) : (
        <section className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 w-full">
          <div className="flex items-center justify-between mb-2">
            <Tooltip text="Set the threshold for flagging students as 'High Risk' on the dashboard.">
              <h2 className="text-lg font-medium leading-6 text-gray-900 dark:text-white cursor-help">
                High Retention Risk Threshold
              </h2>
            </Tooltip>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <div>
              <label
                htmlFor="highRisk"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300"
              >
                High Risk Threshold (Red)
              </label>
              <div className="relative mt-1">
                {riskThresholdLoading ? (
                  <div className="animate-pulse">
                    <div className="h-9 bg-gray-200 dark:bg-gray-600 rounded-md w-full"></div>
                  </div>
                ) : (
                  <input
                    type="number"
                    id="highRisk"
                    value={highRiskThreshold}
                    onChange={(e) =>
                      setHighRiskThreshold(Number(e.target.value))
                    }
                    className={getInputClassName(
                      "block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm sm:text-sm pl-3 pr-8"
                    )}
                  />
                )}
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                  <span className="text-gray-500 dark:text-gray-400 sm:text-sm">
                    %
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Dropout Risk Notifications */}
          <div className="mt-6 border-t border-gray-200 dark:border-gray-700 pt-6">
            <div className="flex items-center justify-between mb-4">
              <Tooltip text="Customize messages sent to students who reach the retention risk threshold. A CRON job runs daily to send these notifications.">
                <h3 className="text-md font-medium leading-6 text-gray-900 dark:text-white cursor-help">
                  Retention Risk Notifications
                </h3>
              </Tooltip>
            </div>

            {/* Notification Method Checkboxes */}
            <div className="mb-4 space-y-2">
              {canSendSms && (
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={sendDropoutSms}
                    onChange={(e) => setSendDropoutSms(e.target.checked)}
                    className="form-checkbox h-5 w-5 text-indigo-600 dark:text-indigo-400"
                  />
                  <span className="text-gray-900 dark:text-white">Send SMS Notification</span>
                </label>
              )}
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={sendDropoutEmail}
                  onChange={(e) => setSendDropoutEmail(e.target.checked)}
                  className="form-checkbox h-5 w-5 text-indigo-600 dark:text-indigo-400"
                />
                <span className="text-gray-900 dark:text-white">Send Email Notification</span>
              </label>
            </div>

            {/* SMS Message */}
            {canSendSms && sendDropoutSms && (
              <div className="mb-4">
                <label
                  htmlFor="dropoutSmsMessage"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                >
                  SMS Message
                </label>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                  Available tags: <code className="bg-gray-100 dark:bg-gray-600 px-1 rounded">{'{{student_name}}'}</code>, <code className="bg-gray-100 dark:bg-gray-600 px-1 rounded">{'{{studio_name}}'}</code>
                </p>
                <textarea
                  id="dropoutSmsMessage"
                  rows={4}
                  value={dropoutSmsMessage}
                  onChange={(e) => setDropoutSmsMessage(e.target.value)}
                  placeholder="Hi {{student_name}}! We noticed you haven't been to class recently. We'd love to see you back at {{studio_name}}!"
                  className={getInputClassName(
                    "block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm sm:text-sm"
                  )}
                />
              </div>
            )}

            {/* Email Message */}
            {sendDropoutEmail && (
              <div className="mb-4">
                <label
                  htmlFor="dropoutEmailMessage"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                >
                  Email Message
                </label>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                  Available tags: <code className="bg-gray-100 dark:bg-gray-600 px-1 rounded">{'{{student_name}}'}</code>, <code className="bg-gray-100 dark:bg-gray-600 px-1 rounded">{'{{studio_name}}'}</code>
                </p>
                <textarea
                  id="dropoutEmailMessage"
                  rows={8}
                  value={dropoutEmailMessage}
                  onChange={(e) => setDropoutEmailMessage(e.target.value)}
                  placeholder="<h2>We Miss You!</h2><p>Hi {{student_name}},</p><p>We noticed you haven't been to class recently at {{studio_name}}. We'd love to see you back!</p><p>If you have any questions, please reach out to us.</p><p>Best regards,<br>The {{studio_name}} Team</p>"
                  className={getInputClassName(
                    "block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm sm:text-sm font-mono"
                  )}
                />
              </div>
            )}
          </div>

          <div className="mt-4 flex justify-end">
            <button
              onClick={handleSaveRiskThresholds}
              className="px-4 py-2 rounded-md bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 hover:bg-gray-100 dark:hover:bg-gray-600 transition"
            >
              <span className="pointer-events-none">Save Risk Settings</span>
            </button>
          </div>
        </section>
        )}

        {/* Lead Visibility in Attendance and Student Sign In */}
        <section className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 w-full">
          <div className="flex items-center justify-between mb-2">
            <Tooltip text="Enable this to display leads in the Attendance and Student Sign-In screens, allowing you to check in leads for classes and letting leads check themselves in.">
              <h2 className="text-lg font-medium leading-6 text-gray-900 dark:text-white cursor-help">
                Lead Visibility in Attendance & Student Sign In
              </h2>
            </Tooltip>
          </div>
          {leadVisibilityLoading ? (
            <div className="flex items-center gap-2 mt-2">
              <div className="h-5 w-5 bg-gray-200 dark:bg-gray-600 rounded animate-pulse"></div>
              <div className="h-4 w-24 bg-gray-200 dark:bg-gray-600 rounded animate-pulse"></div>
            </div>
          ) : (
            <label className="flex items-center gap-2 mt-2">
              <input
                type="checkbox"
                checked={leadVisibility}
                onChange={(e) => handleSaveLeadVisibility(e.target.checked)}
                className="form-checkbox h-5 w-5 text-indigo-600 dark:text-indigo-400"
              />
              <span className="text-gray-900 dark:text-white">Show Leads</span>
            </label>
          )}
        </section>

        {/* Auto Appointment SMS */}
        <section className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 w-full">
          <div className="flex items-center justify-between mb-2">
            <Tooltip text="When enabled, leads who fill out website forms will automatically receive an SMS with a link to book an appointment. This feature requires SMS to be configured (either rent a phone number or use your own Twilio account).">
              <h2 className="text-lg font-medium leading-6 text-gray-900 dark:text-white cursor-help">
                Auto Appointment SMS
              </h2>
            </Tooltip>
          </div>
          <div className="relative">
            {autoAppointmentSmsLoading || smsConfigLoading || planLoading ? (
              <div className="flex items-center gap-2 mt-2">
                <div className="h-5 w-5 bg-gray-200 dark:bg-gray-600 rounded animate-pulse"></div>
                <div className="h-4 w-24 bg-gray-200 dark:bg-gray-600 rounded animate-pulse"></div>
              </div>
            ) : !hasCapability('messagingSms') ? (
              <UpgradePrompt capability="messagingSms" />
            ) : (
              <>
                {!smsConfig.isConfigured && (
                  <div className="mb-6 p-4 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-100 dark:border-indigo-800 rounded flex flex-col items-center text-center">
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                      Set up SMS to enable automatic appointment SMS notifications. Choose to rent a phone number from us (instant setup) or use your own Twilio account.
                    </p>
                    <Link
                      to="/app/settings/integrations"
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                      Go to Integrations Settings
                    </Link>
                  </div>
                )}
                <label className="flex items-center gap-2 mt-2">
                  <input
                    type="checkbox"
                    checked={autoAppointmentSms}
                    onChange={(e) => handleSaveAutoAppointmentSms(e.target.checked)}
                    disabled={!smsConfig.isConfigured}
                    className={`form-checkbox h-5 w-5 text-indigo-600 dark:text-indigo-400 ${
                      !smsConfig.isConfigured ? "opacity-50 cursor-not-allowed" : ""
                    }`}
                  />
                  <span className={`text-gray-900 dark:text-white ${
                    !smsConfig.isConfigured ? "opacity-50" : ""
                  }`}>
                    Send automatic SMS to leads with appointment booking link after they fill out a website form.
                  </span>
                </label>
                
                {autoAppointmentSms && smsConfig.isConfigured && (
                  <div className="mt-4 space-y-6">
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
                      Use <code className="bg-gray-100 dark:bg-gray-600 px-1 rounded">{'{name}'}</code>, <code className="bg-gray-100 dark:bg-gray-600 px-1 rounded">{'{studio_name}'}</code>, and <code className="bg-gray-100 dark:bg-gray-600 px-1 rounded">{'{appointment_link}'}</code> as placeholders. Leave empty to use default message.
                    </p>
                    
                    {/* Initial SMS Message */}
                    <div>
                      <label htmlFor="auto-sms-message" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Initial SMS Message (sent immediately after form submission)
                      </label>
                      <textarea
                        id="auto-sms-message"
                        rows={4}
                        value={autoSmsMessage}
                        onChange={(e) => setAutoSmsMessage(e.target.value)}
                        placeholder="Hi {name}! Thanks for your interest in {studio_name}. We'd love to have you visit us for a class - whether you want to participate, ask questions, or just observe to see if it's right for you. Here are some available times: {appointment_link}"
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white"
                      />
                      <button
                        onClick={handleSaveAutoSmsMessage}
                        disabled={autoSmsMessageLoading}
                        className="mt-2 inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                      >
                        {autoSmsMessageLoading ? "Saving..." : "Save Initial Message"}
                      </button>
                    </div>

                    {/* Follow-up SMS Message #2 */}
                    <div>
                      <label htmlFor="follow-up-sms-message-2" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Follow-up SMS Message #2 (sent 24 hours after initial message)
                      </label>
                      <textarea
                        id="follow-up-sms-message-2"
                        rows={4}
                        value={followUpSmsMessage2}
                        onChange={(e) => setFollowUpSmsMessage2(e.target.value)}
                        placeholder="Hi {name}! Just wanted to make sure you saw our available class times. We'd love to have you visit: {appointment_link} - {studio_name}"
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white"
                      />
                      <button
                        onClick={() => handleSaveFollowUpSmsMessage(2)}
                        disabled={followUpSmsMessage2Loading}
                        className="mt-2 inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                      >
                        {followUpSmsMessage2Loading ? "Saving..." : "Save Follow-up #2"}
                      </button>
                    </div>

                    {/* Follow-up SMS Message #3 */}
                    <div>
                      <label htmlFor="follow-up-sms-message-3" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Follow-up SMS Message #3 (sent 24 hours after follow-up #2)
                      </label>
                      <textarea
                        id="follow-up-sms-message-3"
                        rows={4}
                        value={followUpSmsMessage3}
                        onChange={(e) => setFollowUpSmsMessage3(e.target.value)}
                        placeholder="Hi {name}! We have limited spots available this week. Reserve your appointment here: {appointment_link} - {studio_name}"
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white"
                      />
                      <button
                        onClick={() => handleSaveFollowUpSmsMessage(3)}
                        disabled={followUpSmsMessage3Loading}
                        className="mt-2 inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                      >
                        {followUpSmsMessage3Loading ? "Saving..." : "Save Follow-up #3"}
                      </button>
                    </div>

                    {/* Missed Appointment SMS Message */}
                    <div>
                      <label htmlFor="missed-appointment-sms-message" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Missed Appointment Follow-up SMS (sent when lead misses scheduled trial class)
                      </label>
                      <textarea
                        id="missed-appointment-sms-message"
                        rows={4}
                        value={missedAppointmentSmsMessage}
                        onChange={(e) => setMissedAppointmentSmsMessage(e.target.value)}
                        placeholder="Hi {name}! We noticed you weren't able to make it to your trial class. We'd still love to have you visit us! Book a new time here: {appointment_link} - {studio_name}"
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white"
                      />
                      <button
                        onClick={handleSaveMissedAppointmentSmsMessage}
                        disabled={missedAppointmentSmsMessageLoading}
                        className="mt-2 inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                      >
                        {missedAppointmentSmsMessageLoading ? "Saving..." : "Save Missed Appointment Message"}
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </section>

        {/* Belts & Curriculum */}
        <section className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 w-full">
          <div className="mb-2">
            <div className="mb-4">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                Belts &amp; Curriculum
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Drag and drop belts to arrange them in order of progression. Students will be promoted to the next belt in this sequence.
                Drag and drop curriculum items under each belt to set the order of stripe requirements.
              </p>
            </div>
            <button
              className="flex items-center p-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-100 dark:hover:bg-gray-600 transition text-green-600 dark:text-green-400"
              onClick={() => setShowBeltModal(true)}
            >
              <PlusIcon className="w-5 h-5 mr-1" /> Add Belt
            </button>
          </div>
          {beltsLoading ? (
            <div className="space-y-3">
              {/* Skeleton for 3 belt items */}
              {[1, 2, 3].map((index) => (
                <div key={index} className="flex items-center justify-between py-2">
                  <div className="h-4 w-24 bg-gray-200 dark:bg-gray-600 rounded animate-pulse"></div>
                  <div className="flex gap-2">
                    <div className="h-8 w-8 bg-gray-200 dark:bg-gray-600 rounded animate-pulse"></div>
                    <div className="h-8 w-8 bg-gray-200 dark:bg-gray-600 rounded animate-pulse"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={belts.map(belt => belt.id)}
                strategy={verticalListSortingStrategy}
              >
                <ul className="divide-y divide-gray-200 dark:divide-gray-700">
                  {belts.map((belt: any, index: number) => (
                    <SortableBeltItem
                      key={belt.id}
                      belt={belt}
                      index={index}
                      beltEditId={beltEditId}
                      beltEditValue={beltEditValue}
                      setBeltEditValue={setBeltEditValue}
                      handleSaveBeltEdit={handleSaveBeltEdit}
                      setBeltEditId={setBeltEditId}
                      handleDeleteBelt={handleDeleteBelt}
                      expandedBelts={expandedBelts}
                      toggleBeltExpansion={toggleBeltExpansion}
                      curriculum={curriculum}
                      curriculumLoading={curriculumLoading}
                      curriculumEditId={curriculumEditId}
                      curriculumEditTitle={curriculumEditTitle}
                      curriculumEditDescription={curriculumEditDescription}
                      setCurriculumEditTitle={setCurriculumEditTitle}
                      setCurriculumEditDescription={setCurriculumEditDescription}
                      handleSaveCurriculumEdit={handleSaveCurriculumEdit}
                      handleDeleteCurriculum={handleDeleteCurriculum}
                      handleAddCurriculum={handleAddCurriculum}
                      handleCurriculumDragEnd={handleCurriculumDragEnd}
                      sensors={sensors}
                      addingCurriculumBeltId={addingCurriculumBeltId}
                      newCurriculumTitle={newCurriculumTitle}
                      newCurriculumDescription={newCurriculumDescription}
                      newCurriculumFile={newCurriculumFile}
                      setNewCurriculumFile={setNewCurriculumFile}
                      setNewCurriculumTitle={setNewCurriculumTitle}
                      setNewCurriculumDescription={setNewCurriculumDescription}
                      handleSaveNewCurriculum={handleSaveNewCurriculum}
                      handleCancelAddCurriculum={handleCancelAddCurriculum}
                      curriculumEditAttachmentUrl={curriculumEditAttachmentUrl}
                      curriculumEditAttachmentFilename={curriculumEditAttachmentFilename}
                      curriculumEditNewFile={curriculumEditNewFile}
                      setCurriculumEditNewFile={setCurriculumEditNewFile}
                      curriculumEditClearAttachment={curriculumEditClearAttachment}
                      setCurriculumEditClearAttachment={setCurriculumEditClearAttachment}
                      cancelCurriculumEdit={cancelCurriculumEdit}
                      beginCurriculumEdit={beginCurriculumEdit}
                    />
                  ))}
                </ul>
              </SortableContext>
            </DndContext>
          )}
          <AddItemModal
            open={showBeltModal}
            onClose={() => setShowBeltModal(false)}
            onSave={handleAddBelt}
            label="Belt"
          />
        </section>

        {/* Memberships */}
        <section className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 w-full">
          <div className="mb-2">
            <div className="mb-4">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                Memberships
              </h2>
              {planLoading ? (
                <div className="space-y-2 animate-pulse">
                  <div className="h-4 w-full max-w-2xl bg-gray-200 dark:bg-gray-600 rounded" />
                  <div className="h-4 w-full max-w-xl bg-gray-200 dark:bg-gray-600 rounded" />
                </div>
              ) : canManageStripeMemberships ? (
                <>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Stripe card processing fees are typically 2.9% + $0.30 per successful transaction. If you’d like to recoup processing costs, adjust membership pricing accordingly.{" "}
                    <a
                      href="https://stripe.com/pricing?utm_source=chatgpt.com"
                      target="_blank"
                      rel="noreferrer"
                      className="text-indigo-600 dark:text-indigo-400 hover:underline"
                    >
                      View current merchant processing rates
                    </a>
                    .
                  </p>
                  <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                    To email receipts for membership charges, enable customer receipts directly in your Stripe account. Follow Stripe&apos;s guide here:{" "}
                    <a
                      href="https://docs.stripe.com/payments/advanced/receipts"
                      target="_blank"
                      rel="noreferrer"
                      className="text-indigo-600 dark:text-indigo-400 hover:underline"
                    >
                      Enable payment receipts in Stripe
                    </a>
                    .
                  </p>
                </>
              ) : (
                <>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Define your membership types and monthly prices. Assign them to students on cash or manual billing and record payments as they come in.
                  </p>
                  <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                    Upgrade to Pro to charge memberships automatically via Stripe, send payment setup links, and apply family discounts.{" "}
                    <Link
                      to="/app/settings/billing"
                      className="text-indigo-600 dark:text-indigo-400 hover:underline"
                    >
                      View upgrade options
                    </Link>
                    .
                  </p>
                </>
              )}
            </div>
            <button
              className={`flex items-center p-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-100 dark:hover:bg-gray-600 transition text-green-600 dark:text-green-400 ${
                !canAddMembership ? "opacity-50 cursor-not-allowed" : ""
              }`}
              onClick={() => setShowMembershipModal(true)}
              disabled={!canAddMembership}
            >
              <PlusIcon className="w-5 h-5 mr-1" /> Add Membership
            </button>
          </div>
          <div className="relative">
            {membershipsLoading ? (
              <div className="space-y-3">
                {/* Skeleton for 3 membership items */}
                {[1, 2, 3].map((index) => (
                  <div key={index} className="flex items-center justify-between py-2">
                    <div className="flex items-center gap-3 flex-1">
                      <div className="h-4 w-32 bg-gray-200 dark:bg-gray-600 rounded animate-pulse"></div>
                      <div className="h-4 w-16 bg-gray-200 dark:bg-gray-600 rounded animate-pulse"></div>
                    </div>
                    <div className="flex gap-2">
                      <div className="h-8 w-8 bg-gray-200 dark:bg-gray-600 rounded animate-pulse"></div>
                      <div className="h-8 w-8 bg-gray-200 dark:bg-gray-600 rounded animate-pulse"></div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <>
                {canManageStripeMemberships && !isStripeConnected && (
                  <div className="mb-6 p-4 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-100 dark:border-indigo-800 rounded flex flex-col items-center text-center">
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                      Please connect your Stripe account to manage memberships and
                      pricing.
                    </p>
                    <Link
                      to="/app/settings/integrations"
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                      Go to Integrations Settings
                    </Link>
                  </div>
                )}
                {memberships.length === 0 ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400 py-2">
                    No membership types yet. Add one to assign plans to students.
                  </p>
                ) : (
                <ul className="divide-y divide-gray-200 dark:divide-gray-700">
                  {memberships.map((membership: any) => (
                <li
                  key={membership.id}
                  className="flex items-center justify-between py-2"
                >
                  {membershipEditId === membership.id ? (
                    <div className="flex flex-col xl:flex-row xl:items-center w-full gap-2">
                      <div className="flex flex-col xl:flex-row gap-2 flex-1">
                        <input
                          value={membershipEditValue}
                          onChange={(e) => setMembershipEditValue(e.target.value)}
                          className={getInputClassName(
                            "border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-sm flex-1"
                          )}
                          placeholder="Membership name"
                          disabled={!canAddMembership}
                          maxLength={MAX_LENGTHS.membershipName}
                        />
                        <div className="relative">
                          <span className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-500 dark:text-gray-400 text-sm">
                            $
                          </span>
                          <input
                            type="number"
                            step="0.01"
                            value={membershipEditPrice}
                            onChange={(e) =>
                              setMembershipEditPrice(e.target.value)
                            }
                            className={getInputClassName(
                              "border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-sm w-full xl:w-24 pl-6"
                            )}
                            placeholder="0.00"
                            disabled={!canAddMembership}
                            maxLength={MAX_LENGTHS.membershipPrice}
                          />
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <button
                          className="text-green-600 dark:text-green-400"
                          onClick={() =>
                            handleSaveMembershipEdit(
                              membership.id,
                              membershipEditValue,
                              Number(membershipEditPrice)
                            )
                          }
                          disabled={
                            !canAddMembership ||
                            !membershipEditValue.trim() ||
                            !membershipEditPrice.trim() ||
                            isNaN(Number(membershipEditPrice))
                          }
                        >
                          <CheckIcon className="w-5 h-5" />
                        </button>
                        <button
                          className="text-gray-400 dark:text-gray-500"
                          onClick={() => {
                            setMembershipEditId(null);
                            setMembershipEditValue("");
                            setMembershipEditPrice("");
                          }}
                          disabled={!canAddMembership}
                        >
                          <XMarkIcon className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex w-full items-center justify-between">
                      <span className="text-gray-900 dark:text-white">
                        {DOMPurify.sanitize(membership.name, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] })}{" "}
                        <span className="text-gray-500 dark:text-gray-400 ml-2">
                          ${Number(membership.price).toFixed(2)}/mo
                        </span>
                      </span>
                      <div className="flex gap-2 ml-2">
                        <button
                          className="p-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-100 dark:hover:bg-gray-600 transition text-blue-600 dark:text-blue-400"
                          onClick={() => {
                            setMembershipEditId(membership.id);
                            setMembershipEditValue(membership.name);
                            setMembershipEditPrice(
                              membership.price?.toString() || "0"
                            );
                          }}
                          disabled={!canAddMembership}
                        >
                          <PencilIcon className="w-5 h-5" />
                        </button>
                        <button
                          className="p-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-100 dark:hover:bg-gray-600 transition text-red-600 dark:text-red-400"
                          onClick={() => handleDeleteMembership(membership.id)}
                          disabled={!canAddMembership}
                        >
                          <TrashIcon className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  )}
                </li>
              ))}
                            </ul>
                )}
              </>
            )}
          </div>
        </section>
        <AddMembershipModal
          open={showMembershipModal}
          onClose={() => setShowMembershipModal(false)}
          onSave={handleAddMembership}
          label="Membership"
        />

        {/* Staff Roles */}
        <section className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 w-full">
          <div className="mb-2">
            <div className="mb-4">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                Staff Roles
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Create up to {STAFF_ROLES_LIMIT} staff roles for your studio.
              </p>
            </div>
            <button
              disabled={staffRolesLimitReached}
              className={`flex items-center p-2 border rounded transition ${
                staffRolesLimitReached
                  ? "text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-700 border-gray-200 dark:border-gray-700 cursor-not-allowed"
                  : "text-green-600 dark:text-green-400 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-600"
              }`}
              onClick={() => {
                if (!staffRolesLimitReached) {
                  setShowRoleModal(true);
                } else {
                  setNotification({
                    show: true,
                    message: `You can only create up to ${STAFF_ROLES_LIMIT} staff roles per studio.`,
                    title: "Limit Reached",
                  });
                }
              }}
            >
              <PlusIcon className="w-5 h-5 mr-1" /> Add Staff Role {staffRoles.length}/{STAFF_ROLES_LIMIT}
            </button>
          </div>
          {staffRolesLoading ? (
            <div className="space-y-3">
              {/* Skeleton for 3 staff role items */}
              {[1, 2, 3].map((index) => (
                <div key={index} className="flex items-center justify-between py-2">
                  <div className="h-4 w-24 bg-gray-200 dark:bg-gray-600 rounded animate-pulse"></div>
                  <div className="flex gap-2">
                    <div className="h-8 w-8 bg-gray-200 dark:bg-gray-600 rounded animate-pulse"></div>
                    <div className="h-8 w-8 bg-gray-200 dark:bg-gray-600 rounded animate-pulse"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <ul className="divide-y divide-gray-200 dark:divide-gray-700">
              {staffRoles.map((role: any) => (
                <li
                  key={role.id}
                  className="flex items-center justify-between py-2"
                >
                  {roleEditId === role.id ? (
                    <div className="flex flex-col xl:flex-row xl:items-center w-full gap-2">
                      <input
                        value={roleEditValue}
                        onChange={(e) => setRoleEditValue(e.target.value)}
                        className={getInputClassName(
                          "border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-sm flex-1"
                        )}
                        maxLength={MAX_LENGTHS.roleName}
                      />
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <button
                          className="text-green-600 dark:text-green-400"
                          onClick={() => handleSaveRoleEdit(role.id, roleEditValue)}
                        >
                          <CheckIcon className="w-5 h-5" />
                        </button>
                        <button
                          className="text-gray-400 dark:text-gray-500"
                          onClick={() => setRoleEditId(null)}
                        >
                          <XMarkIcon className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex w-full items-center justify-between">
                      <span className="text-gray-900 dark:text-white">
                        {DOMPurify.sanitize(role.name, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] })}
                      </span>
                      <div className="flex gap-2 ml-2">
                        <button
                          className="p-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-100 dark:hover:bg-gray-600 transition text-blue-600 dark:text-blue-400"
                          onClick={() => {
                            setRoleEditId(role.id);
                            setRoleEditValue(role.name);
                          }}
                        >
                          <PencilIcon className="w-5 h-5" />
                        </button>
                        <button
                          className="p-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-100 dark:hover:bg-gray-600 transition text-red-600 dark:text-red-400"
                          onClick={() => handleDeleteRole(role.id)}
                        >
                          <TrashIcon className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
          <AddItemModal
            open={showRoleModal && !staffRolesLimitReached}
            onClose={() => setShowRoleModal(false)}
            onSave={handleAddRole}
            label="Staff Role"
          />
        </section>

        {/* One-Time Payment Descriptions — hidden, one-time product sales now handled via the Store page
        <section className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 w-full">
          <div className="mb-2">
            <div className="mb-4">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                One-Time Payment Descriptions
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Create up to {PRODUCT_DESCRIPTIONS_LIMIT} one-time payment descriptions for your studio.{" "}
                <strong className="font-semibold text-gray-800 dark:text-gray-100">
                  These are not for the product store. The store products are separate.
                </strong>
              </p>
            </div>
            <button
              disabled={productDescriptionsLimitReached}
              className={`flex items-center p-2 border rounded transition ${
                productDescriptionsLimitReached
                  ? "text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-700 border-gray-200 dark:border-gray-700 cursor-not-allowed"
                  : "text-green-600 dark:text-green-400 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-600"
              }`}
              onClick={() => {
                if (!productDescriptionsLimitReached) {
                  setShowDescriptionModal(true);
                } else {
                  setNotification({
                    show: true,
                    message: `You can only create up to ${PRODUCT_DESCRIPTIONS_LIMIT} one-time payment descriptions per studio.`,
                    title: "Limit Reached",
                  });
                }
              }}
            >
              <PlusIcon className="w-5 h-5 mr-1" /> Add One-Time Payment Description {productDescriptions.length}/{PRODUCT_DESCRIPTIONS_LIMIT}
            </button>
          </div>
          {productDescriptionsLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((index) => (
                <div key={index} className="flex items-center justify-between py-2">
                  <div className="h-4 w-32 bg-gray-200 dark:bg-gray-600 rounded animate-pulse"></div>
                  <div className="flex gap-2">
                    <div className="h-8 w-8 bg-gray-200 dark:bg-gray-600 rounded animate-pulse"></div>
                    <div className="h-8 w-8 bg-gray-200 dark:bg-gray-600 rounded animate-pulse"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <ul className="divide-y divide-gray-200 dark:divide-gray-700">
              {productDescriptions.map((description: any) => (
                <li
                  key={description.id}
                  className="flex items-center justify-between py-2"
                >
                  {descriptionEditId === description.id ? (
                    <div className="flex flex-col xl:flex-row xl:items-center w-full gap-2">
                      <input
                        value={descriptionEditValue}
                        onChange={(e) => setDescriptionEditValue(e.target.value)}
                        className={getInputClassName(
                          "border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-sm flex-1"
                        )}
                        maxLength={MAX_LENGTHS.description}
                      />
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <button
                          className="text-green-600 dark:text-green-400"
                          onClick={() =>
                            handleSaveDescriptionEdit(
                              description.id,
                              descriptionEditValue
                            )
                          }
                        >
                          <CheckIcon className="w-5 h-5" />
                        </button>
                        <button
                          className="text-gray-400 dark:text-gray-500"
                          onClick={() => setDescriptionEditId(null)}
                        >
                          <XMarkIcon className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex w-full items-center justify-between">
                      <span className="text-gray-900 dark:text-white">
                        {DOMPurify.sanitize(description.description, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] })}
                      </span>
                      <div className="flex gap-2 ml-2">
                        <button
                          className="p-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-100 dark:hover:bg-gray-600 transition text-blue-600 dark:text-blue-400"
                          onClick={() => {
                            setDescriptionEditId(description.id);
                            setDescriptionEditValue(description.description);
                          }}
                        >
                          <PencilIcon className="w-5 h-5" />
                        </button>
                        <button
                          className="p-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-100 dark:hover:bg-gray-600 transition text-red-600 dark:text-red-400"
                          onClick={() => handleDeleteDescription(description.id)}
                        >
                          <TrashIcon className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
          <AddItemModal
            open={showDescriptionModal && !productDescriptionsLimitReached}
            onClose={() => setShowDescriptionModal(false)}
            onSave={handleAddDescription}
            label="One-Time Payment Description"
          />
        </section>
        */}

        <StudioClosureDates />

        {/* Sub-Account Management */}
        <section className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 w-full">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            Sub-Account Management
          </h2>
          <SubAccountManagement />
        </section>

        {showDeleteConfirm && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 backdrop-blur-sm transition-opacity" />
            <div className="flex min-h-full items-center justify-center p-4 sm:p-0">
              <div className="relative transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                <div className="absolute right-0 top-0 pr-4 pt-4">
                  <button
                    type="button"
                    className="rounded-md bg-white dark:bg-gray-800 text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                    onClick={() => {
                      setShowDeleteConfirm(false);
                      setItemToDelete(null);
                      setDeleteType(null);
                    }}
                  >
                    <span className="sr-only">Close</span>
                    <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                  </button>
                </div>
                <div>
                  <h3 className="text-base font-semibold leading-6 text-gray-900 dark:text-white">
                    Delete Item
                  </h3>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Are you sure you want to delete this item? This action
                      cannot be undone.
                    </p>
                  </div>
                </div>
                <div className="mt-5 sm:mt-4 flex flex-col xl:flex-row xl:justify-end space-y-3 xl:space-y-0 xl:space-x-3">
                  <button
                    type="button"
                    className="w-full xl:w-auto px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    onClick={() => {
                      setShowDeleteConfirm(false);
                      setItemToDelete(null);
                      setDeleteType(null);
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="w-full xl:w-auto px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                    onClick={confirmDelete}
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
    </div>
  );
}